// Workspace de Modo Estudo dentro do painel novo.
//
// Replica as regras do Modo Estudo do Simulador.html antigo (lista de questões
// filtradas, navegação, validação de resposta, persistência) com visuais novos.
// Roda DENTRO do layout do student-new — breadcrumb e sidebar continuam ativos,
// nada de abrir nova aba.
//
// Fases implementadas:
//   E1 — esqueleto: rota /banks/:bankId/estudo + carregar payload via API
//   E2 — render de questão (read-only) + navegação prev/next + atalhos
//
// Próximas fases: E3 responder + validação, E4 persistência, E5 filtros etc.

import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  BookOpen,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  AlignLeft,
  Table as TableIcon,
  Grid3x3,
  BarChart3,
  List,
  Eye,
  Check,
  X as XIcon,
  RotateCcw,
  Info,
  Search,
  Sparkles,
  SlidersHorizontal,
  CircleCheck,
  CircleX,
  CircleDashed,
  Filter as FilterIcon,
  Clock,
  Hourglass
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { AnimatedTabs } from "@app/ui/components/animated-tabs";
import { api, ApiError, type BankStudyResponse, type QuestaoStudy } from "./api";
import { captureOriginFromEvent, useModalGrowShrink, useBodyScrollLock } from "./modalOrigin";
import { fireCorrectAnimation, vibrate } from "./animations";
import {
  QuestionListSidebar,
  QuestionListBody,
  QuestionListSearchInput,
  type QListStatus,
  type QListAgrupamento
} from "./QuestionListSidebar";
import { QuestionCard } from "./QuestionCard";
import { uiPrefs, toggleQuestionListCollapsed, toggleAiAssistantOpen } from "./uiPrefs";
import { prefs } from "./App";
import { isPlayerOpen } from "./MusicPlayer";
import { WorkspaceHeader, type WorkspaceHeaderAction } from "./WorkspaceHeader";
import { AiAssistant, hydrateChatHistoryFromBank } from "./AiAssistant";
import {
  getStudyTempoMs,
  getStudyTempoTotalMs,
  getStudyCurrentQid,
  initStudyTempo,
  startStudyTempo,
  startStudyTempoTicking,
  stopStudyTempo,
  stopStudyTempoTicking,
  clearStudyTempoForKeys,
  clearStudyTempoAll
} from "./studyTempo";
import { openConfirm } from "./ConfirmPromptModal";

// =====================
// State
// =====================

type Phase = "loading" | "error" | "ready";
export type SyncState = "idle" | "pending" | "saving" | "saved" | "error";

export interface StudyWorkspaceState {
  bankId: string;
  phase: Phase;
  data: BankStudyResponse | null;
  errorMsg: string | null;
  currentIdx: number;
}

export const studyWorkspace = signal<StudyWorkspaceState | null>(null);
export const syncState = signal<SyncState>("idle");
export const lastSavedAt = signal<number | null>(null);
export const mapOpen = signal<boolean>(false);
export const performanceOpen = signal<boolean>(false);
export const questionListMobileOpen = signal<boolean>(false);

// Track largura pra decidir tone do botão Lista. No mobile (<lg), o
// botão sempre representa "abrir modal" — nunca fica primary, porque
// a lista não está visível ao lado da questão. No desktop (lg+), o
// botão é primary quando a lista está aberta.
const isLgViewport = signal<boolean>(
  typeof window !== "undefined" ? window.matchMedia("(min-width: 1024px)").matches : true
);
if (typeof window !== "undefined") {
  const mq = window.matchMedia("(min-width: 1024px)");
  isLgViewport.value = mq.matches;
  const onChange = (e: MediaQueryListEvent) => {
    isLgViewport.value = e.matches;
  };
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else mq.addListener(onChange);
}
export const lightboxImg = signal<{ url: string; legenda?: string } | null>(null);

// =====================
// Roteamento (history.pushState manual)
// =====================

const STUDY_PATH_RE = /^\/student\/banks\/([^/]+)\/estudo\/?$/;

export function enterStudyWorkspace(bankId: string, opts: { pushHistory?: boolean } = {}) {
  studyWorkspace.value = {
    bankId,
    phase: "loading",
    data: null,
    errorMsg: null,
    currentIdx: 0
  };
  if (opts.pushHistory !== false) {
    const url = `/student/banks/${encodeURIComponent(bankId)}/estudo`;
    if (window.location.pathname !== url) {
      window.history.pushState({ studyBankId: bankId }, "", url);
    }
  }
  load(bankId);
}

// Callback opcional disparado quando saímos do workspace (qualquer caminho:
// nav menu, popstate, programático). App.tsx usa pra refazer a list de bancos
// e refletir o progresso recém-salvo. Sem isso o aluno volta pra "Banco de
// questões" e vê stats antigas (respondidas/acertos) — confuso.
let onExitCallback: (() => void) | null = null;
export function onStudyWorkspaceExit(cb: () => void) {
  onExitCallback = cb;
}

export function exitStudyWorkspace(opts: { pushHistory?: boolean } = {}) {
  // Flush ANTES de nulificar o workspace. Sem isso, navegação interna
  // (clique no menu) descartava saves debounced silenciosamente: o timer
  // disparava depois com studyWorkspace.value=null e flushSave bailava no
  // early return de "phase !== ready", perdendo a resposta marcada.
  flushPendingSaveBeforeExit();
  stopStudyTempo();
  stopStudyTempoTicking();
  studyWorkspace.value = null;
  if (opts.pushHistory !== false) {
    if (STUDY_PATH_RE.test(window.location.pathname)) {
      window.history.pushState({}, "", "/student/");
    }
  }
  try { onExitCallback?.(); } catch (err) { console.error("[study] onExit cb falhou:", err); }
}

let popstateBound = false;
export function bindStudyPopstate() {
  if (popstateBound || typeof window === "undefined") return;
  popstateBound = true;
  window.addEventListener("popstate", () => {
    const match = STUDY_PATH_RE.exec(window.location.pathname);
    if (match) {
      const id = decodeURIComponent(match[1]);
      if (!studyWorkspace.value || studyWorkspace.value.bankId !== id) {
        enterStudyWorkspace(id, { pushHistory: false });
      }
    } else {
      if (studyWorkspace.value) {
        // popstate (botão back do browser) também precisa flushar antes de
        // descartar o workspace, pelo mesmo motivo de exitStudyWorkspace.
        flushPendingSaveBeforeExit();
        stopStudyTempo();
        stopStudyTempoTicking();
        studyWorkspace.value = null;
        try { onExitCallback?.(); } catch (err) { console.error("[study] onExit cb falhou:", err); }
      }
    }
  });
}

export function restoreStudyFromUrl() {
  if (typeof window === "undefined") return;
  const match = STUDY_PATH_RE.exec(window.location.pathname);
  if (match) {
    const id = decodeURIComponent(match[1]);
    enterStudyWorkspace(id, { pushHistory: false });
  }
}

async function load(bankId: string) {
  try {
    const res = await api.bankStudy(bankId);
    if (studyWorkspace.value?.bankId !== bankId) return;
    // SAFETY: garante que toda questão tem um id string único e não-vazio.
    // Bancos antigos podem ter q.id undefined/"" — nesse caso TODOS resolvem
    // pra a mesma chave em respostas, e responder 1 marca todas (mesmo bug
    // que existia no modo Prova). Cai pra fallback "q-{idx}" preservando
    // posição.
    //
    // IMPORTANTE: a MESMA regra de fallback ("q-{i}" / "{qid}-i{j}") é
    // replicada em apps/api/src/functions/student-bank-essay.ts::locateTarget
    // pra autosave + grade da dissertativa funcionarem em bancos legados.
    res.questoes = res.questoes.map((q, i) => {
      const qid = typeof q.id === "string" && q.id !== "" ? q.id : `q-${i}`;
      const items = Array.isArray(q.items)
        ? q.items.map((it, j) => ({
            ...it,
            id:
              typeof it.id === "string" && it.id !== ""
                ? it.id
                : `${qid}-i${j}`
          }))
        : q.items;
      return { ...q, id: qid, items };
    });
    // Restaura a última questão vista (lastQid) se houver no estado salvo.
    // Tem que procurar dentro da lista FILTRADA, já que currentIdx fala
    // dela. Se a lastQid foi filtrada por algum filtro ativo, volta pra 0.
    let startIdx = 0;
    const minhas = getMinhasQuestoes(res);
    if (res.estudo.lastQid) {
      const idx = minhas.findIndex((q) => q.id === res.estudo.lastQid);
      if (idx >= 0) startIdx = idx;
    }
    studyWorkspace.value = {
      bankId,
      phase: "ready",
      data: res,
      errorMsg: null,
      currentIdx: startIdx
    };
    // Hidrata conversas com o tutor IA persistidas por questão. Servidor
    // sobrescreve só onde não há conversa local mais recente.
    hydrateChatHistoryFromBank((res as { aiChats?: Parameters<typeof hydrateChatHistoryFromBank>[0] }).aiChats);
    // Tempo por questão — inicializa o tracker do banco e começa a
    // cronometrar a questão inicial. Tick e visibility ficam ligados
    // até exitStudyWorkspace.
    initStudyTempo(bankId);
    const startQ = minhas[startIdx];
    if (startQ) startStudyTempo(bankId, startQ.id);
    startStudyTempoTicking();
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    if (studyWorkspace.value?.bankId !== bankId) return;
    studyWorkspace.value = {
      bankId,
      phase: "error",
      data: null,
      errorMsg: err instanceof Error ? err.message : String(err),
      currentIdx: 0
    };
  }
}

// =====================
// Navegação
// =====================

function gotoIdx(idx: number) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const minhas = getMinhasQuestoes(ws.data);
  const total = minhas.length;
  if (total === 0) return;
  const clamped = Math.max(0, Math.min(total - 1, idx));
  if (clamped === ws.currentIdx) return;
  // Atualiza lastQid pra restaurar nessa questão em refresh.
  const q = minhas[clamped];
  studyWorkspace.value = {
    ...ws,
    currentIdx: clamped,
    data: q
      ? {
          ...ws.data,
          estudo: { ...ws.data.estudo, lastQid: q.id }
        }
      : ws.data
  };
  // Troca a questão sendo cronometrada (flusha a anterior, inicia nova).
  if (q) startStudyTempo(ws.bankId, q.id);
  if (q) scheduleSave(); // gravamos lastQid no servidor mesmo sem responder
}

// Quando navega via setas, o foco fica preso no <button> da alternativa
// que o aluno clicou por último. Como Preact reusa o nó DOM via key={i},
// o foco visual permanece em cima da "A" da nova questão e dá a impressão
// errada de que ela está marcada (focus-ring parece selection). Blurar
// resolve sem mudar o tab order.
function blurActiveAlternative() {
  if (typeof document === "undefined") return;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return;
  if (el.tagName === "BUTTON" || el.tagName === "A") {
    el.blur();
  }
}

function isInteractiveSwipeTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return !!el.closest(
    'button, a, input, textarea, select, label, [role="button"], [data-no-swipe="true"]'
  );
}

function StudySwipeFrame({
  enabled,
  onPrev,
  onNext,
  children
}: {
  enabled: boolean;
  onPrev: () => void;
  onNext: () => void;
  children: any;
}) {
  const startRef = useRef<{ x: number; y: number } | null>(null);

  function onTouchStart(e: any) {
    if (!enabled) return;
    if (e.touches.length !== 1) return;
    if (isInteractiveSwipeTarget(e.target)) return;
    const touch = e.touches[0];
    startRef.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(e: any) {
    if (!enabled || !startRef.current) return;
    const touch = e.changedTouches[0];
    if (!touch) {
      startRef.current = null;
      return;
    }
    const dx = touch.clientX - startRef.current.x;
    const dy = touch.clientY - startRef.current.y;
    startRef.current = null;
    if (Math.abs(dx) < 72) return;
    if (Math.abs(dy) > 48) return;
    if (dx < 0) onNext();
    else onPrev();
  }

  function onTouchCancel() {
    startRef.current = null;
  }

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
    >
      {children}
    </div>
  );
}

function gotoPrev() {
  const ws = studyWorkspace.value;
  if (!ws) return;
  gotoIdx(ws.currentIdx - 1);
}

function gotoNext() {
  const ws = studyWorkspace.value;
  if (!ws) return;
  gotoIdx(ws.currentIdx + 1);
}

// =====================
// Validação de resposta (espelha isAcerto do legado)
// =====================
// q.correta é 1-indexed na payload do banco. q.correta === 0 marca questão
// anulada (qualquer resposta conta como acerto — mas no UI as alternativas
// ficam desabilitadas, replicando o legado).

export function isAnulada(q: QuestaoStudy): boolean {
  return Number(q.correta) === 0;
}

export function correctIdx(q: QuestaoStudy): number {
  const n = Number(q.correta);
  return Number.isFinite(n) && n >= 1 ? n - 1 : -1;
}

// Anulada SEMPRE conta como acerto, mesmo quando não respondida. Regra
// unificada com backend (lib/bank-stats.ts): "anulada já é certa".
export function isAcerto(q: QuestaoStudy, selectedIdx: number | undefined): boolean {
  if (isAnulada(q)) return true;
  if (selectedIdx === undefined) return false;
  return selectedIdx === correctIdx(q);
}

// =====================
// Mutação de respostas + persistência debounced (E4)
// =====================

const SAVE_DEBOUNCE_MS = 1200;
const SAVED_TOAST_LINGER_MS = 2500;
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let savedLingerTimer: ReturnType<typeof setTimeout> | null = null;
// Promise da última save em vôo — usada pra esperar antes da próxima.
let inFlight: Promise<void> | null = null;

function scheduleSave() {
  syncState.value = "pending";
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    void flushSave();
  }, SAVE_DEBOUNCE_MS);
}

async function flushSave(opts: { keepalive?: boolean } = {}): Promise<void> {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const bankId = ws.bankId;
  const estudo = ws.data.estudo;
  // Se já tem save em andamento, espera ela antes de disparar a próxima
  // (evita race que sobrescreveria atualização mais recente).
  if (inFlight) {
    try { await inFlight; } catch { /* ignora */ }
  }
  syncState.value = "saving";
  const doSave = async () => {
    try {
      await api.saveBankProgress(bankId, estudo);
      // Pode ter saído do workspace enquanto salvava — só atualiza UI se
      // continuamos no mesmo banco.
      if (studyWorkspace.value?.bankId === bankId) {
        syncState.value = "saved";
        lastSavedAt.value = Date.now();
        if (savedLingerTimer) clearTimeout(savedLingerTimer);
        savedLingerTimer = setTimeout(() => {
          if (syncState.value === "saved") syncState.value = "idle";
        }, SAVED_TOAST_LINGER_MS);
      }
    } catch (err) {
      console.error("[study] saveBankProgress falhou:", err);
      if (studyWorkspace.value?.bankId === bankId) {
        syncState.value = "error";
      }
    }
  };
  inFlight = doSave().finally(() => {
    inFlight = null;
  });
  if (opts.keepalive) {
    // No path keepalive não esperamos a Promise (browser pode estar fechando).
    return;
  }
  await inFlight;
}

// Flush síncrono ao sair do workspace por navegação SPA interna (clique no
// menu, botão back). Diferente de attemptFlushOnUnload (que cobre fechar
// aba via pagehide/beforeunload), aqui a aba continua aberta — o fetch
// normal completa em background mesmo depois do workspace ser nulificado.
// Captura o estado ANTES de nulificar pra não depender de studyWorkspace.value.
function flushPendingSaveBeforeExit() {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  // Salva se há timer pendente OU se a syncState indica algo não persistido
  // (saving em vôo já está cuidando de si; pending/error precisam de push).
  const hasPending =
    saveTimer != null ||
    syncState.value === "pending" ||
    syncState.value === "error";
  if (!hasPending) return;
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  // Fire-and-forget: a Promise vive enquanto o JS roda na aba. Não
  // bloqueamos a navegação esperando o response.
  void api.saveBankProgress(ws.bankId, ws.data.estudo).catch((err) => {
    console.error("[study] flush on exit falhou:", err);
  });
}

// Tentativa best-effort de salvar antes de descarregar a página. Usa fetch
// com keepalive=true, que o browser sustenta até o request completar mesmo
// após a aba fechar. (sendBeacon usaria POST, mas nosso endpoint é PUT.)
function attemptFlushOnUnload() {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  if (saveTimer || syncState.value === "pending") {
    if (saveTimer) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    try {
      void fetch("/api/student/bank-progress", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ bankId: ws.bankId, estudo: ws.data.estudo }),
        keepalive: true
      }).catch(() => null);
    } catch {
      // Silencioso — beforeunload não tem retry.
    }
  }
}

let unloadBound = false;
export function bindStudyUnloadFlush() {
  if (unloadBound || typeof window === "undefined") return;
  unloadBound = true;
  // pagehide cobre fechar aba + navegação interna mais confiavelmente que
  // beforeunload em mobile.
  window.addEventListener("pagehide", attemptFlushOnUnload);
  window.addEventListener("beforeunload", attemptFlushOnUnload);
}

function setRespostaForCurrent(selectedIdx: number | null) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  // currentIdx aponta para minhasQuestoes (lista filtrada).
  const minhas = getMinhasQuestoes(ws.data);
  const q = minhas[ws.currentIdx];
  if (!q || isAnulada(q)) return; // anuladas não aceitam resposta manual
  const next = { ...ws.data.estudo.respostas };
  if (selectedIdx === null) {
    delete next[q.id];
  } else {
    next[q.id] = selectedIdx;
  }
  const newData: BankStudyResponse = {
    ...ws.data,
    estudo: { ...ws.data.estudo, respostas: next, lastQid: q.id }
  };
  // Pós-resposta o filtro de status pode mover a questão pra fora da lista
  // (ex.: filtro "pendente only" + responder uma → some). Recomputamos pra
  // tentar manter o usuário na mesma questão visualmente, ou na próxima
  // disponível.
  const newMinhas = getMinhasQuestoes(newData);
  let newIdx = ws.currentIdx;
  const stillThere = newMinhas.findIndex((mq) => mq.id === q.id);
  if (stillThere >= 0) {
    newIdx = stillThere;
  } else if (newMinhas.length > 0) {
    newIdx = Math.min(ws.currentIdx, newMinhas.length - 1);
  } else {
    newIdx = 0;
  }
  studyWorkspace.value = { ...ws, data: newData, currentIdx: newIdx };
  scheduleSave();

  // Feedback ao marcar (não em clear/no-op). Animação só dispara em Modo
  // Estudo — esse arquivo todo é o workspace de Estudo, então o gate é
  // implícito (em prova/revisão a UI será outra). Vibração granular via
  // animations.vibrate; o módulo respeita prefs (master + sub-toggle).
  if (selectedIdx !== null) {
    const acertou = isAcerto(q, selectedIdx);
    if (acertou) {
      fireCorrectAnimation();
      vibrate("correct");
    } else {
      vibrate("wrong");
    }
  }
}

function clearCurrentResposta() {
  setRespostaForCurrent(null);
}

/** Todas as chaves de progresso que pertencem a uma questão. Em questão
 *  composta, inclui o q.id (pai) + cada item.id. Servidor usa pra remover
 *  de `respostas` e `essayResponses` simultaneamente. */
function keysForQuestao(q: QuestaoStudy): string[] {
  const keys = [q.id];
  if (Array.isArray(q.items)) {
    for (const it of q.items) {
      if (typeof it.id === "string" && it.id) keys.push(it.id);
    }
  }
  return keys;
}

/** Recomeçar a questão ATUAL. Limpa resposta (MC + items), correção IA
 *  persistida e o tempo cronometrado. Sem confirmação modal — ação é
 *  reversível só se o aluno responder de novo; mas o impacto é local
 *  (não afeta outras questões nem outros alunos). Se quiser modal,
 *  trocar por openConfirm aqui. */
async function resetCurrentQuestion() {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const minhas = getMinhasQuestoes(ws.data);
  const q = minhas[ws.currentIdx];
  if (!q) return;
  const keys = keysForQuestao(q);

  // Atualiza estado local imediatamente — UX percebe instantâneo.
  const novasRespostas: Record<string, number> = {};
  for (const [k, v] of Object.entries(ws.data.estudo.respostas)) {
    if (!keys.includes(k)) novasRespostas[k] = v;
  }
  const novasEssay: Record<string, import("./api").EssayResponseState> = {};
  if (ws.data.estudo.essayResponses) {
    for (const [k, v] of Object.entries(ws.data.estudo.essayResponses)) {
      if (!keys.includes(k)) novasEssay[k] = v;
    }
  }
  studyWorkspace.value = {
    ...ws,
    data: {
      ...ws.data,
      estudo: {
        ...ws.data.estudo,
        respostas: novasRespostas,
        essayResponses: novasEssay
      }
    }
  };
  clearStudyTempoForKeys(keys);

  // Persiste no servidor (best-effort — falhar não desfaz o local porque
  // o aluno já viu a UI limpar; próxima abertura do banco vai re-hidratar
  // do servidor que ainda está com dados antigos. Por isso AVISA no console
  // se falhar — o user pode tentar de novo).
  try {
    await api.clearProgressForKeys(ws.bankId, keys);
  } catch (e) {
    console.error("[reset-question] persistência falhou:", e);
  }
}

/** Recomeçar o BANCO inteiro. Modal de confirmação obrigatório (apaga TUDO:
 *  respostas, correções, tempos). */
async function resetCurrentBank() {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const ok = await openConfirm({
    title: "Recomeçar banco inteiro?",
    message:
      "Vamos apagar TODAS as respostas, correções da IA e tempos cronometrados deste banco. Essa ação não pode ser desfeita. Quer continuar?",
    confirmLabel: "Sim, recomeçar tudo",
    cancelLabel: "Cancelar",
    danger: true
  });
  if (!ok) return;

  // Zera local imediatamente.
  studyWorkspace.value = {
    ...ws,
    data: {
      ...ws.data,
      estudo: {
        ...ws.data.estudo,
        respostas: {},
        essayResponses: {},
        lastQid: ws.data.estudo.lastQid // mantém posição atual pra não desorientar
      }
    }
  };
  clearStudyTempoAll();

  try {
    await api.resetBankProgress(ws.bankId);
  } catch (e) {
    console.error("[reset-bank] persistência falhou:", e);
  }
}

/** Atualiza `essayResponses[key]` em memória após save de texto ou correção
 *  IA. Não dispara save de progresso geral — o endpoint dedicado já persiste
 *  no servidor; aqui só refletimos o estado pra UI mostrar nota/feedback. */
function mergeEssayResponse(
  key: string,
  estado: import("./api").EssayResponseState
) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const prev = ws.data.estudo.essayResponses ?? {};
  const next = { ...prev, [key]: estado };
  studyWorkspace.value = {
    ...ws,
    data: {
      ...ws.data,
      estudo: { ...ws.data.estudo, essayResponses: next }
    }
  };
}

/** Salva resposta de um item dentro de questão composta. Chave em
 *  `respostas` passa a ser o `item.id` (não o `q.id`) — assim aluno pode
 *  marcar a/b/c independente uns dos outros. Mesma persistência, mesmo
 *  pipeline de save debounced. */
function setRespostaForItem(
  q: QuestaoStudy,
  itemId: string,
  selectedIdx: number | null
) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const item = q.items?.find((it) => it.id === itemId);
  if (!item) return;
  // Anulada (correta === 0) sempre conta — não aceita resposta manual.
  if (!item.tipo || item.tipo === "mc") {
    if (item.correta === 0) return;
  }
  const next = { ...ws.data.estudo.respostas };
  if (selectedIdx === null) {
    delete next[itemId];
  } else {
    next[itemId] = selectedIdx;
  }
  const newData: BankStudyResponse = {
    ...ws.data,
    estudo: { ...ws.data.estudo, respostas: next, lastQid: q.id }
  };
  studyWorkspace.value = { ...ws, data: newData };
  scheduleSave();

  // Feedback (acertou/errou) só pra MC items.
  if (selectedIdx !== null && (!item.tipo || item.tipo === "mc")) {
    const corrIdx =
      typeof item.correta === "number" && item.correta >= 1
        ? item.correta - 1
        : -1;
    if (selectedIdx === corrIdx) {
      fireCorrectAnimation();
      vibrate("correct");
    } else {
      vibrate("wrong");
    }
  }
}

// =====================
// Filtros (E5) — espelha applyFiltersAndSort do legado
// =====================

type QStatus = "correct" | "wrong" | "pending";

/** Threshold de aprovação para questões dissertativas e compostas no Modo
 *  Estudo. Usado tanto na agregação ponderada da composta quanto no card de
 *  essay simples — mantenha em um lugar só pra UI e lógica não divergirem. */
export const ESSAY_APPROVAL_THRESHOLD = 7;

export interface CompositeAggregate {
  /** Nota final ponderada, escala 0–10. */
  notaFinal: number;
  /** Soma dos pesos dos items (peso default = 1 quando notaQuestao ausente). */
  totalPeso: number;
  /** Pontos ganhos somados, na mesma escala dos pesos. */
  totalGanho: number;
  /** Existe algum item sem resposta/correção? Quando true, a questão fica
   *  pendente — nota parcial ainda é exibida pra dar feedback de progresso,
   *  mas não conta como certa/errada. */
  pendente: boolean;
  /** Detalhe por item — pra UI mostrar breakdown. */
  itens: Array<{
    id: string;
    label: string;
    kind: "mc" | "essay";
    peso: number;
    ganho: number;
    /** Para essay: nota IA 0..10. Para MC: 10 se acertou, 0 se errou,
     *  10 se anulada, null se sem resposta. */
    notaItem: number | null;
  }>;
}

/** Calcula a nota ponderada de uma questão composta. Pode ser chamada tanto
 *  pra status (questaoStatus) quanto pra UI de breakdown (CompositeAggregateSummary).
 *  Exportada pra ser reusada pelo QuestionCard.tsx. */
export function aggregateCompositeScore(
  q: QuestaoStudy,
  respostas: Record<string, number>,
  essayMap: Record<string, import("./api").EssayResponseState>
): CompositeAggregate {
  const items = q.items ?? [];
  let totalPeso = 0;
  let totalGanho = 0;
  let pendente = false;
  const itens: CompositeAggregate["itens"] = [];

  for (const it of items) {
    const peso =
      typeof it.notaQuestao === "number" && it.notaQuestao > 0 ? it.notaQuestao : 1;
    totalPeso += peso;
    const kind: "mc" | "essay" = it.tipo === "essay" ? "essay" : "mc";

    if (kind === "essay") {
      const grade = essayMap[it.id];
      if (!grade || grade.nota == null) {
        pendente = true;
        itens.push({ id: it.id, label: it.label ?? "—", kind, peso, ganho: 0, notaItem: null });
        continue;
      }
      const ganho = (grade.nota / 10) * peso;
      totalGanho += ganho;
      itens.push({ id: it.id, label: it.label ?? "—", kind, peso, ganho, notaItem: grade.nota });
      continue;
    }

    // MC
    if (it.correta === 0) {
      // Anulada — peso cheio.
      totalGanho += peso;
      itens.push({ id: it.id, label: it.label ?? "—", kind, peso, ganho: peso, notaItem: 10 });
      continue;
    }
    const sel = respostas[it.id];
    if (sel === undefined) {
      pendente = true;
      itens.push({ id: it.id, label: it.label ?? "—", kind, peso, ganho: 0, notaItem: null });
      continue;
    }
    const corrIdx =
      typeof it.correta === "number" && it.correta >= 1 ? it.correta - 1 : -1;
    const acertou = sel === corrIdx;
    const ganho = acertou ? peso : 0;
    totalGanho += ganho;
    itens.push({
      id: it.id,
      label: it.label ?? "—",
      kind,
      peso,
      ganho,
      notaItem: acertou ? 10 : 0
    });
  }

  const notaFinal = totalPeso > 0 ? (totalGanho / totalPeso) * 10 : 0;
  return { notaFinal, totalPeso, totalGanho, pendente, itens };
}

function questaoStatus(q: QuestaoStudy, respostas: Record<string, number>): QStatus {
  // Anulada vai pro grupo "correct" sempre (mesmo sem resposta marcada).
  if (isAnulada(q)) return "correct";

  // Mapa de essay grades vem do workspace ativo. Lemos via signal pra
  // não precisar passar como argumento em todo callsite — essa função é
  // chamada de muitos lugares (filtro, badge, mini-summary).
  const ws = studyWorkspace.value;
  const essayMap = ws?.data?.estudo?.essayResponses ?? {};

  // Questão composta: agrega via MÉDIA PONDERADA pelos pesos (notaQuestao)
  // de cada item. Cada item contribui (nota/10) * peso (essay) ou peso
  // cheio se acertou (MC) / 0 se errou. Anulada conta como peso cheio.
  // Threshold final ≥7 (0..10) pra ser considerada correta. Sem
  // notaQuestao definido, assume peso 1 — todos os items iguais.
  if (Array.isArray(q.items) && q.items.length >= 2) {
    const agg = aggregateCompositeScore(q, respostas, essayMap);
    if (agg.pendente) return "pending";
    return agg.notaFinal >= 7 ? "correct" : "wrong";
  }

  // Questão essay simples: lê nota IA persistida em q.id.
  if (q.tipo === "essay") {
    const grade = essayMap[q.id];
    if (!grade || grade.nota == null) return "pending";
    return grade.nota >= 7 ? "correct" : "wrong";
  }

  // MC simples.
  const sel = respostas[q.id];
  if (sel === undefined) return "pending";
  if (isAcerto(q, sel)) return "correct";
  return "wrong";
}

// Mapeia o status interno do Study (correct/wrong/pending) pro vocabulário do
// QuestionListSidebar (correct/wrong/answered/pending/anulada). Anulada vira
// um status próprio aqui (em vez de "correct") porque a lista mostra a tag
// "ANUL" e a stripe âmbar dedicada.
function mapStudyStatus(
  q: QuestaoStudy,
  respostas: Record<string, number>
): QListStatus {
  if (isAnulada(q)) return "anulada";
  // Em questão composta, segue a regra agregada de questaoStatus mas
  // sem o caso "anulada" (a questão pai geralmente não é anulada — só
  // items individuais podem ser).
  if (Array.isArray(q.items) && q.items.length >= 2) {
    const agg = questaoStatus(q, respostas);
    return agg; // correct | wrong | pending — sidebar trata como QListStatus
  }
  const sel = respostas[q.id];
  if (sel === undefined) return "pending";
  return isAcerto(q, sel) ? "correct" : "wrong";
}

// O Study guarda agrupamento como "tema" | "origem" | null (legado, persiste
// na DB). O componente compartilhado usa "tema" | "fonte" e nunca null
// (sempre tem que estar agrupado por algo). Quando o valor persistido é null,
// default visual = "fonte" (mesma numeração original do banco). Pedido Karol
// 2026-05-24 — antes era "tema" e estava confundindo a ordenação esperada.
function agrupamentoToShared(
  a: "tema" | "origem" | null
): QListAgrupamento {
  if (a === "tema") return "tema";
  // null OU "origem" → "fonte" (ordem original do banco)
  return "fonte";
}
function agrupamentoToInternal(
  a: QListAgrupamento
): "tema" | "origem" | null {
  return a === "fonte" ? "origem" : a;
}

function questaoHasImages(q: QuestaoStudy): boolean {
  if (typeof q.imagem_url === "string" && q.imagem_url) return true;
  return Array.isArray(q.imagens) && q.imagens.length > 0;
}

function questaoHasTabelas(q: QuestaoStudy): boolean {
  return Array.isArray(q.tabelas) && q.tabelas.length > 0;
}

function matchesFiltro(
  q: QuestaoStudy,
  filtros: BankStudyResponse["estudo"]["filtros"],
  respostas: Record<string, number>
): boolean {
  if (filtros.temas.length > 0 && (!q.tema || !filtros.temas.includes(q.tema))) {
    return false;
  }
  if (filtros.fontes.length > 0 && (!q.fonte || !filtros.fontes.includes(q.fonte))) {
    return false;
  }
  if (filtros.dificuldade.length > 0 && filtros.dificuldade.length < 5) {
    const d = Number(q.dificuldade ?? 0);
    if (!filtros.dificuldade.includes(d)) return false;
  }
  if (filtros.somenteImagem && !questaoHasImages(q)) return false;
  if (filtros.somenteCaso && !q.caso) return false;
  if (filtros.somenteTabela && !questaoHasTabelas(q)) return false;
  // Filtro por tipo. Lista vazia = todos (default). Default da questão = "mc".
  if (filtros.tipos && filtros.tipos.length > 0) {
    const t = (q.tipo === "essay" ? "essay" : "mc") as "mc" | "essay";
    if (!filtros.tipos.includes(t)) return false;
  }
  if (filtros.busca) {
    const term = filtros.busca.toLowerCase();
    const haystack = [q.pergunta, q.caso ?? "", ...(q.opcoes ?? [])].join(" ").toLowerCase();
    if (!haystack.includes(term)) return false;
  }
  if (filtros.status.length > 0) {
    const st = questaoStatus(q, respostas);
    if (!filtros.status.includes(st)) return false;
  }
  return true;
}

function getMinhasQuestoes(data: BankStudyResponse): QuestaoStudy[] {
  const filtered = data.questoes.filter((q) =>
    matchesFiltro(q, data.estudo.filtros, data.estudo.respostas)
  );
  return applyAgrupamento(filtered, data.estudo.agrupamento);
}

// Ordena a lista filtrada conforme agrupamento ('tema' | 'origem' | null).
// Preserva ordem original quando não tem agrupamento.
function applyAgrupamento(
  qs: QuestaoStudy[],
  agrupamento: "tema" | "origem" | null
): QuestaoStudy[] {
  if (!agrupamento) return qs;
  const key = agrupamento === "tema" ? "tema" : "fonte";
  // Sort estável (Array.prototype.sort no V8 é estável desde Node 11)
  return [...qs].sort((a, b) => {
    const av = (a[key] ?? "").toString();
    const bv = (b[key] ?? "").toString();
    return av.localeCompare(bv, "pt-BR");
  });
}

function setAgrupamento(agrupamento: "tema" | "origem" | null) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  if (ws.data.estudo.agrupamento === agrupamento) return;
  // Mantém questão atual em foco se possível.
  const oldMinhas = getMinhasQuestoes(ws.data);
  const oldQ = oldMinhas[ws.currentIdx] ?? null;
  const newData: BankStudyResponse = {
    ...ws.data,
    estudo: { ...ws.data.estudo, agrupamento }
  };
  const newMinhas = getMinhasQuestoes(newData);
  let newIdx = 0;
  if (oldQ) {
    const found = newMinhas.findIndex((q) => q.id === oldQ.id);
    if (found >= 0) newIdx = found;
  }
  studyWorkspace.value = { ...ws, data: newData, currentIdx: newIdx };
  scheduleSave();
}

// Mutador genérico de filtros — qualquer mudança no objeto filtros passa
// por aqui, que: 1) substitui o objeto no signal, 2) ajusta currentIdx pra
// continuar apontando pra mesma questão (se ela sobreviveu ao filtro novo),
// 3) dispara save debounced.
function updateFiltros(
  patch: Partial<BankStudyResponse["estudo"]["filtros"]>
) {
  const ws = studyWorkspace.value;
  if (!ws || ws.phase !== "ready" || !ws.data) return;
  const oldFiltros = ws.data.estudo.filtros;
  const newFiltros = { ...oldFiltros, ...patch };

  // Identidade da questão atual ANTES do filtro (pra tentar manter focada).
  const oldMinhas = getMinhasQuestoes(ws.data);
  const oldCurrentQ = oldMinhas[ws.currentIdx] ?? null;

  const newData: BankStudyResponse = {
    ...ws.data,
    estudo: { ...ws.data.estudo, filtros: newFiltros }
  };
  const newMinhas = getMinhasQuestoes(newData);
  let newIdx = 0;
  if (oldCurrentQ) {
    const found = newMinhas.findIndex((q) => q.id === oldCurrentQ.id);
    if (found >= 0) newIdx = found;
  }
  if (newMinhas.length === 0) newIdx = 0;

  studyWorkspace.value = { ...ws, data: newData, currentIdx: newIdx };
  scheduleSave();
}

// Toggle "inclusivo": semântica = "se array vazio, tudo visível".
// Comportamento:
//   - Array vazio (tudo visível): clicar significa "quero filtrar este OUT"
//     → retorna allItems menos o clicado.
//   - Array com itens, e clicado está dentro: remove.
//   - Array com itens, e clicado fora: adiciona. Se atingir TODOS os itens,
//     colapsa pra [] (volta ao modo "tudo visível").
function toggleInclusiveFilter<T>(current: T[], allItems: T[], item: T): T[] {
  if (current.length === 0) {
    return allItems.filter((x) => x !== item);
  }
  if (current.includes(item)) {
    return current.filter((x) => x !== item);
  }
  const next = [...current, item];
  if (next.length >= allItems.length) return [];
  return next;
}

function uniqueTemasOf(qs: QuestaoStudy[]): string[] {
  const out: string[] = [];
  for (const q of qs) if (q.tema && !out.includes(q.tema)) out.push(q.tema);
  return out;
}

function uniqueFontesOf(qs: QuestaoStudy[]): string[] {
  const out: string[] = [];
  for (const q of qs) if (q.fonte && !out.includes(q.fonte)) out.push(q.fonte);
  return out;
}

function toggleTema(tema: string) {
  const ws = studyWorkspace.value;
  if (!ws?.data) return;
  const all = uniqueTemasOf(ws.data.questoes);
  updateFiltros({
    temas: toggleInclusiveFilter(ws.data.estudo.filtros.temas, all, tema)
  });
}

/** Tipos de questão presentes no banco. Vazio se não consegue inferir. */
function uniqueTiposOf(qs: QuestaoStudy[]): Array<"mc" | "essay"> {
  let hasMc = false;
  let hasEssay = false;
  for (const q of qs) {
    if (q.tipo === "essay") hasEssay = true;
    else hasMc = true;
    if (hasMc && hasEssay) break;
  }
  const out: Array<"mc" | "essay"> = [];
  if (hasMc) out.push("mc");
  if (hasEssay) out.push("essay");
  return out;
}

function toggleTipo(t: "mc" | "essay") {
  const ws = studyWorkspace.value;
  if (!ws?.data) return;
  const all = uniqueTiposOf(ws.data.questoes);
  // Se só há um tipo no banco, alternar não faz sentido — vira no-op.
  if (all.length < 2) return;
  updateFiltros({
    tipos: toggleInclusiveFilter(ws.data.estudo.filtros.tipos ?? [], all, t)
  });
}

function toggleFonte(fonte: string) {
  const ws = studyWorkspace.value;
  if (!ws?.data) return;
  const all = uniqueFontesOf(ws.data.questoes);
  updateFiltros({
    fontes: toggleInclusiveFilter(ws.data.estudo.filtros.fontes, all, fonte)
  });
}

function toggleDificuldade(d: number) {
  const f = studyWorkspace.value?.data?.estudo.filtros;
  if (!f) return;
  updateFiltros({
    dificuldade: toggleInclusiveFilter(f.dificuldade, [1, 2, 3, 4, 5], d)
  });
}

const ALL_STATUSES: QStatus[] = ["correct", "wrong", "pending"];
function toggleStatus(s: QStatus) {
  const f = studyWorkspace.value?.data?.estudo.filtros;
  if (!f) return;
  updateFiltros({
    status: toggleInclusiveFilter(f.status, ALL_STATUSES, s)
  });
}

function clearAllFilters() {
  updateFiltros({
    temas: [],
    fontes: [],
    dificuldade: [1, 2, 3, 4, 5],
    busca: "",
    somenteImagem: false,
    somenteCaso: false,
    somenteTabela: false,
    tipos: [],
    status: []
  });
}

// Conta quantos filtros estão ativos (pra mostrar badge "limpar")
function activeFilterCount(filtros: BankStudyResponse["estudo"]["filtros"]): number {
  let n = 0;
  n += filtros.temas.length > 0 ? 1 : 0;
  n += filtros.fontes.length > 0 ? 1 : 0;
  n += filtros.dificuldade.length > 0 && filtros.dificuldade.length < 5 ? 1 : 0;
  n += filtros.busca ? 1 : 0;
  n += filtros.somenteImagem ? 1 : 0;
  n += filtros.somenteCaso ? 1 : 0;
  n += filtros.somenteTabela ? 1 : 0;
  n += filtros.tipos && filtros.tipos.length > 0 ? 1 : 0;
  n += filtros.status.length > 0 ? 1 : 0;
  return n;
}

// Atalhos de teclado (← → / A-E / 1-5). Ligado uma vez no boot.
let kbBound = false;
export function bindStudyKeyboard() {
  if (kbBound || typeof window === "undefined") return;
  kbBound = true;
  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (!studyWorkspace.value || studyWorkspace.value.phase !== "ready") return;
    // Player aberto consome todos os atalhos (setas, M, Esc, A-E etc).
    if (isPlayerOpen.value) return;
    const t = e.target as HTMLElement | null;
    if (t?.isContentEditable || t?.tagName === "INPUT" || t?.tagName === "TEXTAREA") return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      blurActiveAlternative();
      gotoPrev();
      return;
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      blurActiveAlternative();
      gotoNext();
      return;
    }
    // M abre/fecha o mapa de questões
    if (e.key === "m" || e.key === "M") {
      e.preventDefault();
      mapOpen.value = !mapOpen.value;
      return;
    }
    // Esc fecha lightbox > mapa > desempenho > filtros > lista mobile
    if (e.key === "Escape") {
      if (lightboxImg.value) {
        e.preventDefault();
        lightboxImg.value = null;
        return;
      }
      if (mapOpen.value) {
        e.preventDefault();
        mapOpen.value = false;
        return;
      }
      if (performanceOpen.value) {
        e.preventDefault();
        performanceOpen.value = false;
        return;
      }
      if (filtersOpen.value) {
        e.preventDefault();
        filtersOpen.value = false;
        return;
      }
      if (questionListMobileOpen.value) {
        e.preventDefault();
        questionListMobileOpen.value = false;
        return;
      }
    }

    // A-E ou a-e
    if (/^[a-eA-E]$/.test(e.key)) {
      const idx = e.key.toUpperCase().charCodeAt(0) - 65;
      const ws = studyWorkspace.value;
      const minhas = ws.data ? getMinhasQuestoes(ws.data) : [];
      const q = minhas[ws.currentIdx];
      if (q && idx < q.opcoes.length) {
        e.preventDefault();
        setRespostaForCurrent(idx);
      }
      return;
    }
    // 1-5 (top row)
    if (/^[1-5]$/.test(e.key)) {
      const idx = Number(e.key) - 1;
      const ws = studyWorkspace.value;
      const minhas = ws.data ? getMinhasQuestoes(ws.data) : [];
      const q = minhas[ws.currentIdx];
      if (q && idx < q.opcoes.length) {
        e.preventDefault();
        setRespostaForCurrent(idx);
      }
      return;
    }
  });
}

// =====================
// KaTeX via CDN (E8)
// =====================
// Carrega KaTeX + auto-render extension uma vez, sob demanda. O markdown
// mínimo abaixo NÃO toca em $...$ — KaTeX faz auto-render sobre o DOM
// renderizado depois.

let katexLoadPromise: Promise<void> | null = null;
const KATEX_VERSION = "0.16.11";

export function ensureKaTeX(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (katexLoadPromise) return katexLoadPromise;
  katexLoadPromise = new Promise<void>((resolve, reject) => {
    // CSS (idempotente)
    if (!document.querySelector('link[data-katex="1"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.css`;
      link.dataset.katex = "1";
      document.head.appendChild(link);
    }
    // Main script
    const main = document.createElement("script");
    main.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/katex.min.js`;
    main.onload = () => {
      // Auto-render extension
      const ar = document.createElement("script");
      ar.src = `https://cdn.jsdelivr.net/npm/katex@${KATEX_VERSION}/dist/contrib/auto-render.min.js`;
      ar.onload = () => resolve();
      ar.onerror = () => reject(new Error("KaTeX auto-render falhou ao carregar"));
      document.head.appendChild(ar);
    };
    main.onerror = () => reject(new Error("KaTeX falhou ao carregar"));
    document.head.appendChild(main);
  }).catch((err) => {
    console.warn("[study] KaTeX:", err);
    // Não bloqueia render — só não vai ter math bonita.
  });
  return katexLoadPromise;
}

// Macros KaTeX para notação brasileira de provas. Por padrão o KaTeX só
// conhece `\sin`, `\cos`, `\tan` (notação internacional), mas provas BR
// escrevem `sen`, `tg`, `cotg`, `cossec`, `arcsen`, `arctg`. Sem esses
// aliases, o LLM seria forçado a usar `\operatorname{sen}` e a fidelidade
// visual fica frágil. Cada macro vira `\operatorname{...}` que renderiza
// em modo "função" (fonte reta, espaçamento certo).
const KATEX_BR_MACROS: Record<string, string> = {
  "\\sen": "\\operatorname{sen}",
  "\\tg": "\\operatorname{tg}",
  "\\cotg": "\\operatorname{cotg}",
  "\\cossec": "\\operatorname{cossec}",
  "\\arcsen": "\\operatorname{arcsen}",
  "\\arctg": "\\operatorname{arctg}",
  "\\arccotg": "\\operatorname{arccotg}",
  // mdc/mmc — convenções BR
  "\\mdc": "\\operatorname{mdc}",
  "\\mmc": "\\operatorname{mmc}"
};

export function renderMathIn(el: HTMLElement | null) {
  if (!el || typeof window === "undefined") return;
  const w = window as unknown as {
    renderMathInElement?: (el: HTMLElement, opts: unknown) => void;
  };
  if (typeof w.renderMathInElement !== "function") return;
  try {
    w.renderMathInElement(el, {
      delimiters: [
        { left: "$$", right: "$$", display: true },
        { left: "$", right: "$", display: false },
        { left: "\\(", right: "\\)", display: false },
        { left: "\\[", right: "\\]", display: true }
      ],
      macros: KATEX_BR_MACROS,
      throwOnError: false
    });
  } catch (err) {
    console.warn("[study] KaTeX render falhou:", err);
  }
}

// =====================
// Markdown mínimo (inline + block)
// =====================
// Cobre **bold**, *italic*, `code`, e quebras de linha. KaTeX e listas
// vêm em E8 (polimento). Escapa HTML antes pra evitar XSS — payload do banco
// vem do servidor, mas é responsabilidade do servidor sanitizar; aqui é
// defesa em profundidade.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Mapeamento de cores semânticas — tokens estáveis que o prompt de extração
// usa pra preservar cores significativas da prova (ex.: "a primeira letra é
// AZUL e a segunda VERMELHA"). Usar nomes em português evita confusão na
// hora do LLM gerar o JSON. Cores baseadas em paleta tailwind 600 (light)
// que tem contraste em fundo branco E não some no dark mode.
// Paleta de cores semânticas. As 8 canônicas são tailwind-600 (contraste OK
// em light E dark). Sinônimos comuns + variações regionais mapeiam pra cor
// próxima — meta é NUNCA mostrar `[xyz]A[/xyz]` literal pro usuário, mesmo
// que o banco use uma cor não-prevista.
const COLOR_TOKENS: Record<string, string> = {
  // 8 canônicas
  azul: "#2563eb",
  vermelho: "#dc2626",
  verde: "#059669",
  amarelo: "#ca8a04",
  laranja: "#ea580c",
  roxo: "#7c3aed",
  rosa: "#db2777",
  cinza: "#6b7280",
  // extras canônicas
  preto: "#18181b",
  branco: "#737373", // legível no light/dark
  marrom: "#92400e",
  // sinônimos & variações comuns em PT-BR
  azulado: "#2563eb",
  azul_claro: "#3b82f6",
  azul_escuro: "#1e3a8a",
  navy: "#1e3a8a",
  marinho: "#1e3a8a",
  indigo: "#4f46e5",
  ciano: "#0891b2",
  turquesa: "#0d9488",
  vermelhinho: "#dc2626",
  vermelhao: "#b91c1c",
  bordo: "#9f1239",
  bordô: "#9f1239",
  vinho: "#9f1239",
  escarlate: "#dc2626",
  carmim: "#dc2626",
  esverdeado: "#059669",
  esmeralda: "#10b981",
  verde_claro: "#10b981",
  verde_escuro: "#047857",
  oliva: "#65a30d",
  limao: "#84cc16",
  amarelado: "#ca8a04",
  dourado: "#ca8a04",
  ouro: "#ca8a04",
  bege: "#a8a29e",
  alaranjado: "#ea580c",
  ambar: "#d97706",
  ferrugem: "#c2410c",
  arroxeado: "#7c3aed",
  lilas: "#a855f7",
  lilás: "#a855f7",
  violeta: "#7c3aed",
  purpura: "#7c2d92",
  púrpura: "#7c2d92",
  magenta: "#c026d3",
  fucsia: "#d946ef",
  fúcsia: "#d946ef",
  rosado: "#db2777",
  acinzentado: "#6b7280",
  prata: "#94a3b8",
  prateado: "#94a3b8",
  grafite: "#374151",
  chumbo: "#475569",
  bronze: "#92400e"
};

// Normaliza um nome de cor extraído de `[cor]…[/cor]` ou `{{color:cor}}…`
// pro slug canônico do COLOR_TOKENS. Estratégia em camadas:
//   1) lower + strip accents → exact match
//   2) substring: se o nome desconhecido CONTÉM um canônico (ex: "azulado"
//      contém "azul"), usa o canônico
//   3) devolve null — chamador decide o fallback (renderizar sem cor)
const COLOR_CANON_NAMES = [
  "azul", "vermelho", "verde", "amarelo", "laranja", "roxo", "rosa", "cinza",
  "preto", "branco", "marrom", "ciano", "turquesa", "lilas", "violeta",
  "bordo", "vinho", "dourado", "bege", "ambar", "magenta", "prata", "navy"
];
// Combining diacritical marks (U+0300–U+036F). Construído via RegExp() pra
// evitar surpresa de encoding em editor (Windows às vezes salva como CP1252
// e quebra regex literal com chars unicode).
const ACCENT_RE = new RegExp("[\\u0300-\\u036f]", "g");

// Sufixos PT-BR comuns que viram derivados (azul→azulado, vermelho→vermelhinho,
// roxo→arroxeado/aroxeado, verde→esverdeado). Removemos antes de tentar match
// pra `arroxeado` cair em `roxo` mesmo sem listar todas as variações.
const COLOR_STEM_STRIPS = [
  /^a/,                  // "arroxeado" → "rroxeado" → contém "roxo"? não. "rosado"→"osado"
  /[_-]?claro$/,
  /[_-]?escuro$/,
  /[_-]?intenso$/,
  /[_-]?vivo$/,
  /ado$/,                // "azulado" → "azul"
  /ido$/,
  /oso$/,
  /inho$/,
  /zinho$/,
  /ada$/
];

function normalizeColorName(raw: string): string | null {
  const clean = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(ACCENT_RE, "")
    .replace(/\s+/g, "_");
  // 1) exact
  if (COLOR_TOKENS[clean]) return clean;
  // 2) substring contra canônicas — captura "azulado" → azul, "esverdeado" → verde
  for (const canon of COLOR_CANON_NAMES) {
    if (clean.includes(canon)) return canon;
  }
  // 3) tira sufixos e tenta de novo (pra pegar derivados que não cairam no #2)
  for (const re of COLOR_STEM_STRIPS) {
    const stem = clean.replace(re, "");
    if (stem !== clean && stem.length >= 3) {
      if (COLOR_TOKENS[stem]) return stem;
      for (const canon of COLOR_CANON_NAMES) {
        if (stem.includes(canon)) return canon;
      }
    }
  }
  return null;
}

export function renderInline(raw: string): string {
  let s = escapeHtml(raw);
  // ORDEM IMPORTA: marcadores estendidos primeiro (==, ++, ~~) pra não
  // colidirem com bold (**), italic (*) ou underscore-italic.
  // `code` — escapes preservados pq escapeHtml já rodou.
  s = s.replace(
    /`([^`\n]+)`/g,
    '<code class="rounded bg-accent px-1 py-0.5 text-[0.92em]">$1</code>'
  );
  // Shim de tolerância: bancos extraídos por LLM frequentemente vêm com
  // `[cor]texto[/cor]` em vez do marcador oficial `{{color:cor}}…{{/color}}`.
  // Aceitamos QUALQUER palavra dentro dos colchetes (\w + acentos + _ + -) —
  // `normalizeColorName` decide se conhece a cor, mapeia sinônimo
  // ("azulado"→azul, "lilás"→roxo) ou retorna null.
  //
  // Se a cor for desconhecida, devolvemos o match ORIGINAL — o regex de
  // `{{color}}` abaixo já tem fallback que renderiza o texto sem cor (e o
  // shim aqui não cria brackets visíveis pro usuário).
  //
  // Restritivo o suficiente pra não comer markdown link `[label](url)` ou
  // footnote `[^foot]` (precisa `[/word]` no fechamento, que esses não têm).
  s = s.replace(
    /\[\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]([\s\S]*?)\[\s*\/\s*([a-zA-ZÀ-ÿ_-]{2,24})\s*\]/g,
    (_full, openName: string, inner: string) => {
      const canonical = normalizeColorName(openName);
      if (canonical) return `{{color:${canonical}}}${inner}{{/color}}`;
      // Cor não reconhecida (mesmo após sinônimos/stem-strip): renderiza só
      // o texto interno em <strong> pra preservar a ÊNFASE que o autor
      // queria, mas sem brackets visíveis. Princípio: nunca quebrado.
      return `<strong>${inner}</strong>`;
    }
  );
  // {{color:nome}}texto{{/color}} — marcadores semânticos de cor preservando
  // o significado visual da prova (ex.: "AAAA com 3 azuis e 1 vermelha").
  // Cor inválida → renderiza só o texto interno (fail-safe, nada quebrado).
  s = s.replace(
    /\{\{color:([a-zA-ZÀ-ÿ_-]+)\}\}([\s\S]*?)\{\{\/color\}\}/gi,
    (_m, name: string, inner: string) => {
      const canonical = normalizeColorName(name) ?? name.toLowerCase();
      const c = COLOR_TOKENS[canonical];
      if (!c) return inner;
      return `<span style="color:${c};font-weight:600;">${inner}</span>`;
    }
  );
  // ==realce== (highlight/mark)
  s = s.replace(/==([^=\n]+)==/g, "<mark>$1</mark>");
  // ++sublinhado++
  s = s.replace(/\+\+([^+\n]+)\+\+/g, "<u>$1</u>");
  // ~~tachado~~
  s = s.replace(/~~([^~\n]+)~~/g, "<s>$1</s>");
  // **negrito** (e __negrito__ alternativo)
  s = s.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_\n]+)__/g, "<strong>$1</strong>");
  // *italic* — não pega ** já consumido
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  // _italic_ — só se cercado por whitespace/início/pontuação pra não destruir
  // T_f, x_1 etc. Mesmo padrão do legado.
  s = s.replace(
    /(^|[\s>([{,;:!?])_([^_\n\\^]+)_(?=[\s<)\]}.,;:!?]|$)/g,
    "$1<em>$2</em>"
  );
  // [label](url) — só http/https pra evitar javascript: e similares.
  s = s.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_m, label, url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline">${label}</a>`
  );
  return s;
}

export function renderBlock(raw: string): string {
  // Quebra em parágrafos por linha em branco, aplica inline em cada.
  const paragraphs = raw.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return paragraphs
    .map((p) => {
      // Dentro do parágrafo, \n vira <br>.
      const lines = p.split("\n").map(renderInline).join("<br>");
      return `<p>${lines}</p>`;
    })
    .join("");
}

// =====================
// UI
// =====================

export function StudyWorkspaceView() {
  const ws = studyWorkspace.value;
  if (!ws) return null;
  return (
    <>
      {ws.phase === "loading" && <StudyLoading />}
      {ws.phase === "error" && <StudyError msg={ws.errorMsg} />}
      {ws.phase === "ready" && ws.data && (
        <StudyReady data={ws.data} currentIdx={ws.currentIdx} />
      )}
      {ws.phase === "ready" && ws.data && mapOpen.value && (
        <QuestionMapModal data={ws.data} currentIdx={ws.currentIdx} />
      )}
      {ws.phase === "ready" && ws.data && performanceOpen.value && (
        <PerformanceModal data={ws.data} />
      )}
      {ws.phase === "ready" && ws.data && questionListMobileOpen.value && (
        <QuestionListMobileModal data={ws.data} currentIdx={ws.currentIdx} />
      )}
      {ws.phase === "ready" && ws.data && <FilterPanel data={ws.data} />}
      {lightboxImg.value && <Lightbox image={lightboxImg.value} />}
    </>
  );
}

// =====================
// Drawer mobile da lista de questões
// =====================
// No desktop a lista vive na sidebar direita (QuestionList). No mobile abre
// como bottom-sheet via botão "Lista" no header.

function QuestionListMobileModal({
  data,
  currentIdx
}: {
  data: BankStudyResponse;
  currentIdx: number;
}) {
  const isOpen = questionListMobileOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (questionListMobileOpen.value = false)
  );
  const close = requestClose;
  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }
  const minhas = getMinhasQuestoes(data);
  const agr = data.estudo.agrupamento;
  return (
    <div
      class="fixed inset-0 z-[70] grid min-h-full place-items-end overflow-y-auto scroll-thin bg-black/60 backdrop-blur-sm sm:items-center sm:place-items-center sm:p-4"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Lista de questões"
    >
      <div
        ref={ref}
        class="flex max-h-[85vh] w-full flex-col overflow-hidden rounded-t-lg border-x border-t border-border bg-card shadow-2xl sm:max-w-lg sm:rounded-md sm:border"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5">
          <div class="flex items-center gap-2">
            <List width="1.3em" height="1.3em" className="text-primary" />
            <h2 class="text-sm font-bold">
              Questões{" "}
              <span class="text-xs font-medium text-muted-foreground">
                ({minhas.length})
              </span>
            </h2>
          </div>
          <button
            type="button"
            aria-label="Fechar"
            onClick={close}
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </header>

        <div class="space-y-2 border-b border-border px-5 py-3">
          {/* Toggle Tema/Fonte com indicador deslizante. AnimatedTabs size=sm
              dá o mesmo look-feel do segmented antigo + slide animado. */}
          <AnimatedTabs<"tema" | "origem">
            ariaLabel="Agrupar lista por"
            size="sm"
            variant="strong"
            className="w-full"
            items={[
              { id: "origem", label: "Fonte" },
              { id: "tema", label: "Tema" }
            ]}
            value={agr === "origem" ? "origem" : "tema"}
            onChange={setAgrupamento}
          />
          <QuestionListSearchInput />
        </div>

        <div class="flex flex-1 min-h-0 flex-col overflow-hidden">
          <QuestionListBody
            questoes={minhas}
            currentIdx={currentIdx}
            onPick={(i) => {
              gotoIdx(i);
              close();
            }}
            statusOf={(q) => mapStudyStatus(q, data.estudo.respostas)}
            agrupamento={agrupamentoToShared(agr)}
            numOf={(q: any) => {
              const idxFonte = data.questoes.findIndex((eq) => eq.id === q.id);
              const n = (q as any).numero_original;
              return n != null && n !== "" ? n : idxFonte >= 0 ? idxFonte + 1 : "?";
            }}
          />
        </div>
      </div>
    </div>
  );
}

function MobileSortButton({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      class={`flex-1 rounded px-2 py-1 font-medium transition-colors ${
        active
          ? "bg-accent text-foreground shadow-sm"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

// =====================
// Modal Ver Desempenho (substitui as ProgressBars que viviam na tela)
// =====================
// Mostra um resumo do desempenho do aluno no banco: total respondido,
// % de acerto, distribuição por status e quebra por tema (top temas
// mais respondidos).

// Quebra estats por algum agrupador (tema, fonte, dificuldade).
// Retorna ordenado por % ASC = piores primeiro. Ignora grupos sem resposta.
function breakdownBy(
  questoes: QuestaoStudy[],
  respostas: Record<string, number>,
  keyFn: (q: QuestaoStudy) => string
) {
  const map = new Map<string, { c: number; w: number; p: number; t: number }>();
  for (const q of questoes) {
    const k = keyFn(q);
    const slot = map.get(k) ?? { c: 0, w: 0, p: 0, t: 0 };
    const st = questaoStatus(q, respostas);
    if (st === "correct") slot.c++;
    else if (st === "wrong") slot.w++;
    else slot.p++;
    slot.t++;
    map.set(k, slot);
  }
  // pct = acertos / total do grupo (não só respondidas), regra unificada com
  // backend (anuladas contam como acerto e denominador é total). Filtra
  // grupos onde nada foi feito ainda (sem acertos nem erros nem anuladas).
  return Array.from(map.entries())
    .map(([label, s]) => ({
      label,
      c: s.c,
      w: s.w,
      p: s.p,
      t: s.t,
      pct: s.t > 0 ? Math.round((s.c / s.t) * 100) : null,
      respondidas: s.c + s.w
    }))
    .filter((t) => t.c + t.w > 0)
    .sort((a, b) => (a.pct ?? 100) - (b.pct ?? 100));
}

function PerformanceModal({ data }: { data: BankStudyResponse }) {
  const isOpen = performanceOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (performanceOpen.value = false)
  );
  const close = requestClose;
  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }
  // Conta em duas dimensões: TOTAL do banco e ESCOPO atual (filtros).
  const respostas = data.estudo.respostas;
  function tally(qs: QuestaoStudy[]) {
    let c = 0, w = 0, p = 0;
    for (const q of qs) {
      const st = questaoStatus(q, respostas);
      if (st === "correct") c++;
      else if (st === "wrong") w++;
      else p++;
    }
    return { correct: c, wrong: w, pending: p, total: qs.length };
  }
  const totalStats = tally(data.questoes);
  const filtroSemStatus = { ...data.estudo.filtros, status: [] };
  const escopo = data.questoes.filter((q) =>
    matchesFiltro(q, filtroSemStatus, respostas)
  );
  const escopoStats = tally(escopo);
  const isFiltered = escopo.length < data.questoes.length;
  // % acerto: acertos (já inclui anuladas via questaoStatus → "correct") sobre
  // o TOTAL do escopo. Regra unificada com backend: anulada conta sempre e o
  // denominador é o universo total, não só o respondido. Ver lib/bank-stats.ts.
  const pctAcertoTotal = totalStats.total > 0
    ? Math.round((totalStats.correct / totalStats.total) * 100)
    : null;
  const pctAcertoEscopo = escopoStats.total > 0
    ? Math.round((escopoStats.correct / escopoStats.total) * 100)
    : null;

  const temasArr = breakdownBy(data.questoes, respostas, (q) => q.tema ?? "Sem tema").slice(0, 10);
  const fontesArr = breakdownBy(data.questoes, respostas, (q) => q.fonte ?? "Sem fonte").slice(0, 10);
  const dificuldadeArr = breakdownBy(
    data.questoes,
    respostas,
    (q) => (q.dificuldade != null ? `${q.dificuldade}` : "Sem dif.")
  ).sort((a, b) => a.label.localeCompare(b.label));

  // Por tipo de conteúdo
  const tipoStats = [
    { label: "Situação", qs: data.questoes.filter((q) => !!q.caso) },
    { label: "Imagem", qs: data.questoes.filter(questaoHasImages) },
    { label: "Tabela", qs: data.questoes.filter(questaoHasTabelas) },
    { label: "Sem extras", qs: data.questoes.filter((q) => !q.caso && !questaoHasImages(q) && !questaoHasTabelas(q)) }
  ]
    .map(({ label, qs }) => {
      const sub = tally(qs);
      const r = sub.correct + sub.wrong;
      return {
        label,
        c: sub.correct,
        w: sub.wrong,
        p: sub.pending,
        t: qs.length,
        pct: r > 0 ? Math.round((sub.correct / r) * 100) : null,
        respondidas: r
      };
    })
    .filter((t) => t.t > 0);

  return (
    <div
      class="fixed inset-0 z-[70] grid min-h-full place-items-start overflow-y-auto scroll-thin bg-black/60 p-4 backdrop-blur-sm sm:place-items-center"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Ver desempenho"
    >
      <div
        ref={ref}
        class="w-full max-w-3xl overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        <header class="flex items-center justify-between gap-3 border-b border-border px-6 py-4">
          <div class="flex items-center gap-3">
            <div class="grid size-9 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
              <BarChart3 width="1.3em" height="1.3em" />
            </div>
            <h2 class="text-base font-bold">Ver desempenho</h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <XIcon className="size-4" />
          </button>
        </header>

        <div class="space-y-5 px-6 py-5">
          {/* Cards de resumo */}
          <section class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <PerfCard
              label="Acertos"
              value={escopoStats.correct}
              tone="emerald"
            />
            <PerfCard
              label="Erros"
              value={escopoStats.wrong}
              tone="red"
            />
            <PerfCard
              label="Pendentes"
              value={escopoStats.pending}
              tone="muted"
            />
            <PerfCard
              label="% Acerto"
              value={pctAcertoEscopo != null ? `${pctAcertoEscopo}%` : "—"}
              tone="primary"
            />
          </section>

          {isFiltered && (
            <div class="rounded-md border border-border bg-accent/20 px-4 py-2.5 text-xs text-muted-foreground">
              Estatísticas acima são do escopo filtrado ({escopoStats.total} de{" "}
              {totalStats.total} questões). No banco inteiro:{" "}
              <span class="font-medium text-foreground">
                {totalStats.correct} acertos
              </span>{" "}
              ·{" "}
              <span class="font-medium text-foreground">
                {totalStats.wrong} erros
              </span>{" "}
              ·{" "}
              <span class="font-medium text-foreground">
                {pctAcertoTotal != null ? `${pctAcertoTotal}%` : "—"} acerto
              </span>
            </div>
          )}

          {escopoStats.correct + escopoStats.wrong === 0 ? (
            <div class="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
              Você ainda não respondeu nenhuma questão. Comece a responder pra
              ver seu desempenho aqui.
            </div>
          ) : (
            <div class="space-y-5">
              {temasArr.length > 0 && (
                <PerfBreakdownSection
                  title="Por tema"
                  hint="piores primeiro"
                  rows={temasArr}
                />
              )}
              {fontesArr.length > 1 && (
                <PerfBreakdownSection
                  title="Por fonte"
                  hint="piores primeiro"
                  rows={fontesArr}
                />
              )}
              {dificuldadeArr.length > 0 && (
                <PerfBreakdownSection
                  title="Por dificuldade"
                  hint="1 (fácil) → 5 (difícil)"
                  rows={dificuldadeArr}
                  kind="difficulty"
                />
              )}
              {tipoStats.length > 0 && (
                <PerfBreakdownSection
                  title="Por tipo de conteúdo"
                  hint=""
                  rows={tipoStats}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PerfCard({
  label,
  value,
  tone
}: {
  label: string;
  value: number | string;
  tone: "emerald" | "red" | "muted" | "primary";
}) {
  const toneBg =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : tone === "red"
      ? "bg-red-500/10 text-red-600 dark:text-red-400"
      : tone === "primary"
      ? "bg-primary/10 text-primary"
      : "bg-muted/40 text-muted-foreground";
  return (
    <div class={`rounded-md border border-border px-4 py-3 ${toneBg}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div class="mt-0.5 text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}

interface BreakdownRow {
  label: string;
  c: number;
  w: number;
  p: number;
  t: number;
  pct: number | null;
  respondidas: number;
}

function PerfBreakdownSection({
  title,
  hint,
  rows,
  kind
}: {
  title: string;
  hint: string;
  rows: BreakdownRow[];
  kind?: "difficulty";
}) {
  return (
    <section>
      <header class="mb-2 flex items-center justify-between">
        <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {hint && (
          <span class="text-[10px] text-muted-foreground/70">{hint}</span>
        )}
      </header>
      <div class="space-y-1.5">
        {rows.map((r) => (
          <PerfBreakdownRow key={r.label} {...r} kind={kind} />
        ))}
      </div>
    </section>
  );
}

function PerfBreakdownRow({
  label,
  c,
  w,
  t,
  pct,
  respondidas,
  kind
}: BreakdownRow & { kind?: "difficulty" }) {
  // Pra "Por dificuldade", troca o número (1..5) pela mesma badge de estrelas
  // usada nos chips da questão — visual coerente com a UI da prova/estudo.
  const isDifStars =
    kind === "difficulty" && /^[1-5]$/.test(label);
  return (
    <div class="flex items-center gap-3">
      <span class="flex w-32 shrink-0 items-center text-xs font-medium text-foreground sm:w-40" title={label}>
        {isDifStars ? (
          <DifficultyStars value={Number(label)} />
        ) : (
          <span class="truncate">{label}</span>
        )}
      </span>
      <div class="flex h-2 flex-1 overflow-hidden rounded-full bg-muted/40">
        <span
          class="h-full bg-emerald-500"
          style={{ width: `${t > 0 ? (c / t) * 100 : 0}%` }}
        />
        <span
          class="h-full bg-red-500"
          style={{ width: `${t > 0 ? (w / t) * 100 : 0}%` }}
        />
      </div>
      <span class="w-24 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
        {pct != null ? `${pct}%` : "—"}{" "}
        <span class="opacity-60">
          ({respondidas}/{t})
        </span>
      </span>
    </div>
  );
}

// =====================
// Lightbox (E8)
// =====================

export function Lightbox({ image }: { image: { url: string; legenda?: string } }) {
  // Tranca scroll do body enquanto a lightbox está montada — sem isso, a
  // roda do mouse rola a página atrás em vez de fazer zoom.
  useBodyScrollLock(true);
  // Zoom + pan: signals locais. Roda do mouse zoom, click+drag pan.
  // Reset ao trocar de imagem ou abrir o lightbox.
  const zoomLevel = useRef<{ scale: number; x: number; y: number }>({
    scale: 1,
    x: 0,
    y: 0
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const dragging = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  function applyTransform() {
    const el = imgRef.current;
    if (!el) return;
    const { scale, x, y } = zoomLevel.current;
    el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
    el.style.cursor = scale > 1 ? (dragging.current ? "grabbing" : "grab") : "zoom-in";
  }

  function setZoom(next: number, originX?: number, originY?: number) {
    const prev = zoomLevel.current.scale;
    const clamped = Math.max(1, Math.min(5, next));
    if (clamped === prev) return;
    // Mantém ponto de origem do zoom (mouse) estável visualmente.
    if (originX != null && originY != null && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const cx = originX - rect.left - rect.width / 2;
      const cy = originY - rect.top - rect.height / 2;
      const ratio = clamped / prev;
      zoomLevel.current.x = (zoomLevel.current.x - cx) * ratio + cx;
      zoomLevel.current.y = (zoomLevel.current.y - cy) * ratio + cy;
    } else {
      zoomLevel.current.x *= clamped / prev;
      zoomLevel.current.y *= clamped / prev;
    }
    zoomLevel.current.scale = clamped;
    if (clamped === 1) {
      zoomLevel.current.x = 0;
      zoomLevel.current.y = 0;
    }
    applyTransform();
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.85 : 1.18;
    setZoom(zoomLevel.current.scale * delta, e.clientX, e.clientY);
  }

  function onPointerDown(e: PointerEvent) {
    if (zoomLevel.current.scale <= 1) return;
    dragging.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: zoomLevel.current.x,
      baseY: zoomLevel.current.y
    };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    applyTransform();
  }
  function onPointerMove(e: PointerEvent) {
    if (!dragging.current) return;
    zoomLevel.current.x = dragging.current.baseX + (e.clientX - dragging.current.startX);
    zoomLevel.current.y = dragging.current.baseY + (e.clientY - dragging.current.startY);
    applyTransform();
  }
  function onPointerUp() {
    dragging.current = null;
    applyTransform();
  }

  function onDoubleClick(e: MouseEvent) {
    if (zoomLevel.current.scale > 1) {
      setZoom(1);
    } else {
      setZoom(2.5, e.clientX, e.clientY);
    }
  }

  // Reset ao trocar de imagem
  useEffect(() => {
    zoomLevel.current = { scale: 1, x: 0, y: 0 };
    applyTransform();
  }, [image.url]);

  function close() {
    lightboxImg.value = null;
  }
  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/85 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Imagem ampliada"
    >
      {/* Controles topo direito */}
      <div class="absolute right-4 top-4 flex items-center gap-2">
        <ZoomControlButton
          label="Diminuir zoom (-)"
          onClick={() => setZoom(zoomLevel.current.scale - 0.4)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"><circle cx="11" cy="11" r="7" /><line x1="8" y1="11" x2="14" y2="11" /><line x1="20" y1="20" x2="17" y2="17" /></svg>
        </ZoomControlButton>
        <ZoomControlButton
          label="Aumentar zoom (+)"
          onClick={() => setZoom(zoomLevel.current.scale + 0.4)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"><circle cx="11" cy="11" r="7" /><line x1="8" y1="11" x2="14" y2="11" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="20" y1="20" x2="17" y2="17" /></svg>
        </ZoomControlButton>
        <ZoomControlButton label="Resetar zoom" onClick={() => setZoom(1)}>
          <RotateCcw className="size-4" />
        </ZoomControlButton>
        <ZoomControlButton label="Fechar" onClick={close}>
          <XIcon className="size-4" />
        </ZoomControlButton>
      </div>

      <figure class="flex max-h-full max-w-full flex-col items-center gap-3 overflow-hidden">
        <img
          ref={imgRef}
          src={image.url}
          alt={image.legenda ?? "Imagem"}
          class="max-h-[82vh] max-w-full select-none rounded-md object-contain shadow-2xl"
          style={{ transform: "translate(0,0) scale(1)", cursor: "zoom-in", transition: "transform 0.06s ease-out" }}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDblClick={onDoubleClick}
          draggable={false}
        />
        {image.legenda && (
          <figcaption
            class="max-w-2xl rounded-md bg-card/90 px-4 py-2 text-center text-sm text-foreground shadow-lg"
            dangerouslySetInnerHTML={{ __html: renderInline(image.legenda) }}
          />
        )}
      </figure>

      {/* Dica de uso */}
      <div class="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-card/80 px-3 py-1.5 text-[11px] text-muted-foreground shadow">
        Roda do mouse pra zoom &middot; duplo-clique alterna &middot; arrastar pan
      </div>
    </div>
  );
}

function ZoomControlButton({
  label,
  onClick,
  children
}: {
  label: string;
  onClick: () => void;
  children: any;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      class="grid size-9 place-items-center rounded-full bg-card/90 text-foreground shadow-lg transition-colors hover:bg-card"
    >
      {children}
    </button>
  );
}

// Skeleton rico que espelha a estrutura final (tags + pergunta + alternativas +
// barra de navegação). Sem spinner — o legado nunca exibia ícone estático, o
// loading era puro placeholder. Mantém o aluno orientado sobre o que está
// sendo carregado sem competir visualmente.
function StudyLoading() {
  return (
    <div class="space-y-5">
      {/* Header */}
      <header class="flex items-center gap-3">
        <Skeleton className="size-10 rounded-md" />
        <div class="flex-1 space-y-2">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-3.5 w-40" />
        </div>
        <Skeleton className="hidden h-8 w-24 rounded-md sm:block" />
      </header>

      <div class="flex flex-col gap-5 lg:flex-row lg:items-start">
        {/* Lista de questões à esquerda (desktop) */}
        <aside class="hidden rounded-md border border-border bg-card/50 p-3 lg:flex lg:w-80 lg:shrink-0 lg:flex-col lg:gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} class="flex items-start gap-2 rounded-md px-2 py-2">
              <Skeleton className="size-2.5 shrink-0 rounded-full" />
              <div class="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-40" />
                <Skeleton className="h-3 w-full" />
              </div>
            </div>
          ))}
        </aside>

        {/* Questão central */}
        <div class="min-w-0 flex-1 space-y-5">
          <div class="space-y-4 rounded-md border border-border bg-card/40 p-5">
            {/* Tags */}
            <div class="flex flex-wrap gap-1.5">
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            {/* Pergunta */}
            <div class="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            {/* Alternativas */}
            <ul class="space-y-2 pt-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <li key={i} class="flex items-center gap-3 rounded-md border border-border bg-background/60 px-4 py-3">
                  <Skeleton className="size-7 shrink-0 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                </li>
              ))}
            </ul>
          </div>
          {/* Barra de navegação */}
          <div class="flex items-center justify-between">
            <Skeleton className="h-9 w-28 rounded-md" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-9 w-28 rounded-md" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StudyError({ msg }: { msg: string | null }) {
  return (
    <div class="grid place-items-center py-16">
      <div class="max-w-md space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-6 py-8 text-center">
        <AlertCircle className="mx-auto size-8 text-destructive" />
        <h2 class="text-base font-bold">Não consegui carregar o banco</h2>
        <p class="text-sm text-muted-foreground">{msg ?? "Erro desconhecido."}</p>
      </div>
    </div>
  );
}

function StudyReady({ data, currentIdx }: { data: BankStudyResponse; currentIdx: number }) {
  const minhas = getMinhasQuestoes(data);
  const total = minhas.length;
  const q = minhas[currentIdx];
  const selected = q ? data.estudo.respostas[q.id] : undefined;
  return (
    <div class="space-y-5">
      {/* Pílula flutuante de sync (bottom-left). Fixed-positioned, fica fora
          do fluxo. Renderiza condicionalmente quando syncState !== idle. */}
      <SyncIndicator />
      <StudyHeader
        bank={data.bank}
        currentIdx={currentIdx}
        total={total}
        totalUnfiltered={data.questoes.length}
        data={data}
      />
      <div
        class={`flex flex-col gap-5 lg:items-start ${
          uiPrefs.value.questionListSide === "right"
            ? "lg:flex-row-reverse"
            : "lg:flex-row"
        }`}
      >
        {/* Lista de questões — lado controlado por uiPrefs.questionListSide */}
        <QuestionListSidebar
          questoes={getMinhasQuestoes(data)}
          currentIdx={currentIdx}
          onPick={(idx) => gotoIdx(idx)}
          statusOf={(q) => mapStudyStatus(q, data.estudo.respostas)}
          tempoMsOf={(qq) => getStudyTempoMs(qq.id)}
          agrupamento={agrupamentoToShared(data.estudo.agrupamento)}
          onAgrupamentoChange={(a) => setAgrupamento(agrupamentoToInternal(a))}
          numOf={(q: any) => {
            const idxFonte = data.questoes.findIndex((eq) => eq.id === q.id);
            const n = (q as any).numero_original;
            return n != null && n !== "" ? n : idxFonte >= 0 ? idxFonte + 1 : "?";
          }}
          countLabel={`${getMinhasQuestoes(data).length}`}
        />
        <div class="min-w-0 flex-1 space-y-5">
          {total === 0 ? (
            <NoQuestionsForFilter />
          ) : q ? (
            <StudySwipeFrame
              enabled={prefs.value.swipe && total > 1}
              onPrev={gotoPrev}
              onNext={gotoNext}
            >
              <QuestionCard
                q={q}
                selected={selected}
                onSelect={(i) => setRespostaForCurrent(i)}
                onClear={clearCurrentResposta}
                feedback="answered"
                showCorrectWhenWrong
                showStatusSummary
                showExplanation
                tempoMs={getStudyTempoMs(q.id)}
                respostasMap={data.estudo.respostas}
                onSelectItem={(itemId, i) => setRespostaForItem(q, itemId, i)}
                onClearItem={(itemId) => setRespostaForItem(q, itemId, null)}
                bankId={data.bank.id}
                essayResponses={data.estudo.essayResponses ?? {}}
                onEssayChange={(key, st) => mergeEssayResponse(key, st)}
                onResetQuestion={() => void resetCurrentQuestion()}
              />
            </StudySwipeFrame>
          ) : (
            <EmptyState />
          )}
          {total > 0 && <NavigationBar currentIdx={currentIdx} total={total} />}
        </div>
        {/* Assistente IA — sidebar oposta à lista de questões. Visível
            só quando aluno habilitou + abriu. */}
        <AiAssistant
          question={q ?? null}
          respostaAluno={typeof selected === "number" ? selected : null}
          bankId={data.bank.id}
        />
      </div>
    </div>
  );
}

function NoQuestionsForFilter() {
  return (
    <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center">
      <FilterIcon className="mx-auto mb-3 size-8 text-muted-foreground/60" />
      <h2 class="text-base font-bold">Nenhuma questão bate com os filtros</h2>
      <p class="mt-1 text-sm text-muted-foreground">
        Ajuste ou limpe os filtros pra voltar a ver questões.
      </p>
      <button
        type="button"
        onClick={clearAllFilters}
        class="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent"
      >
        <RotateCcw width="1.3em" height="1.3em" /> Limpar todos os filtros
      </button>
    </div>
  );
}

function StudyHeader({
  bank,
  currentIdx,
  total,
  totalUnfiltered,
  data
}: {
  bank: BankStudyResponse["bank"];
  currentIdx: number;
  total: number;
  totalUnfiltered: number;
  data: BankStudyResponse;
}) {
  const isFiltered = total < totalUnfiltered;
  const filterCount = activeFilterCount(data.estudo.filtros);

  const subtitle =
    total === 0 ? (
      <>Nenhuma questão visível</>
    ) : (
      <>
        Questão{" "}
        <span class="font-bold text-foreground">
          {Math.min(currentIdx + 1, total)}
        </span>{" "}
        de <span class="font-bold text-foreground">{total}</span>
        {isFiltered && (
          <span class="ml-1.5 text-muted-foreground/70">
            (de {totalUnfiltered} no banco)
          </span>
        )}
      </>
    );

  const actions: WorkspaceHeaderAction[] = [
    {
      id: "filtros",
      icon: SlidersHorizontal,
      label: "Filtros",
      title: "Filtros",
      onClick: (e) => {
        captureOriginFromEvent(e);
        filtersOpen.value = true;
      },
      tone: filterCount > 0 ? "amber" : "neutral",
      badge: filterCount > 0 ? String(filterCount) : undefined
    },
    {
      id: "desempenho",
      icon: BarChart3,
      label: "Desempenho",
      title: "Ver desempenho",
      onClick: (e) => {
        captureOriginFromEvent(e);
        performanceOpen.value = true;
      }
    },
    {
      id: "mapa",
      icon: Grid3x3,
      label: "Mapa",
      title: "Mapa de questões (M)",
      onClick: (e) => {
        captureOriginFromEvent(e);
        mapOpen.value = true;
      }
    },
    {
      id: "lista",
      icon: List,
      label: "Lista",
      title: uiPrefs.value.questionListCollapsed
        ? "Mostrar lista de questões"
        : "Esconder lista de questões",
      onClick: (e) => {
        captureOriginFromEvent(e);
        if (window.matchMedia("(max-width: 1023px)").matches) {
          questionListMobileOpen.value = true;
        } else {
          toggleQuestionListCollapsed();
        }
      },
      // Tone só fica primary no DESKTOP quando a lista está visível ao
      // lado. No mobile, a lista nunca está visível ao lado da questão
      // (só abre em modal sob demanda), então sempre neutral.
      tone:
        isLgViewport.value && !uiPrefs.value.questionListCollapsed
          ? "primary"
          : "neutral"
    }
  ];
  if (uiPrefs.value.aiAssistantEnabled) {
    // Por enquanto, o Assistente IA só funciona bem em desktop (sidebar
    // lateral). hideOnMobile esconde o botão em telas < md e o painel
    // próprio do AiAssistant já é `hidden lg:flex`. Quando tivermos uma
    // UX mobile dedicada (drawer cheio, sheet de baixo etc.), removemos.
    actions.push({
      id: "ia",
      icon: Sparkles,
      label: "IA & notas",
      title: uiPrefs.value.aiAssistantOpen
        ? "Fechar painel de IA e anotações"
        : "Abrir painel de IA e anotações",
      onClick: () => toggleAiAssistantOpen(),
      tone: uiPrefs.value.aiAssistantOpen ? "primary" : "neutral",
      hideOnMobile: true
    });
  }

  // Recomeçar banco — destrutivo (limpa tudo), por isso fica entre as
  // últimas ações com tone vermelho. Confirmação modal está dentro do
  // resetCurrentBank.
  actions.push({
    id: "recomecar-banco",
    icon: RotateCcw,
    label: "Recomeçar",
    title: "Recomeçar banco — apaga respostas, correções e tempos",
    onClick: () => void resetCurrentBank(),
    tone: "danger"
  });

  return (
    <WorkspaceHeader
      mode="study"
      icon={BookOpen}
      iconTone="blue"
      title={bank.name}
      subtitle={subtitle}
      summary={<PerformanceMiniSummary data={data} />}
      timers={total > 0 ? <StudyTimers /> : undefined}
      actions={actions}
    />
  );
}

// Format ms → HH:MM:SS (ou MM:SS se < 1h). Espelha o `formatHMS` da Prova,
// duplicado aqui pra não puxar todo o ProvaWorkspace só por uma função.
function formatHMS(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Cronômetros do Modo Estudo (Atual + Total). Subscritos ao studyTempoTick via
// getStudyTempoMs/getStudyTempoTotalMs — atualizam de segundo em segundo.
// Sem ciclo de modos como na Prova: aqui é referência, não vale nota.
function StudyTimers() {
  const qid = getStudyCurrentQid();
  const atualMs = qid ? getStudyTempoMs(qid) : 0;
  const totalMs = getStudyTempoTotalMs();
  // Coluna: chip "Questão atual" em cima do "Total". Ambos com w-full
  // pra estirar até a largura do maior, deixando o conjunto alinhado.
  return (
    <div class="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[160px]">
      <StudyTimerChip
        label="Questão atual"
        tooltip="Tempo cronometrado na questão aberta agora"
        icon={Clock}
        value={qid ? formatHMS(atualMs) : "—"}
        emphasis="primary"
      />
      <StudyTimerChip
        label="Total"
        tooltip="Tempo total cronometrado neste banco (todas as questões)"
        icon={Hourglass}
        value={formatHMS(totalMs)}
      />
    </div>
  );
}

function StudyTimerChip({
  label,
  tooltip,
  icon: Icon,
  value,
  emphasis
}: {
  label: string;
  tooltip: string;
  icon: typeof Clock;
  value: string;
  emphasis?: "primary";
}) {
  const tone =
    emphasis === "primary"
      ? "border-primary/40 bg-primary/5 text-foreground"
      : "border-border bg-background text-foreground/85";
  return (
    <span
      title={tooltip}
      class={`flex h-12 w-full items-center gap-2 rounded-md border px-3 text-sm leading-none ${tone}`}
    >
      <Icon className="size-4 shrink-0 text-primary" strokeWidth={2.25} />
      <span class="flex min-w-0 flex-col items-start gap-0.5">
        <span class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span class="font-bold tabular-nums">{value}</span>
      </span>
    </span>
  );
}

// Resumo inline compacto que fica ao lado do botão "Desempenho". Visível só
// no desktop (lg:flex) porque no mobile ocuparia espaço demais — lá o usuário
// abre o modal pelo botão. Conta o escopo filtrado (igual ao modal), mostra
// valor absoluto + % entre parênteses.
function PerformanceMiniSummary({ data }: { data: BankStudyResponse }) {
  const respostas = data.estudo.respostas;
  const filtroSemStatus = { ...data.estudo.filtros, status: [] };
  const escopo = data.questoes.filter((q) =>
    matchesFiltro(q, filtroSemStatus, respostas)
  );
  let c = 0, w = 0, p = 0;
  for (const q of escopo) {
    const st = questaoStatus(q, respostas);
    if (st === "correct") c++;
    else if (st === "wrong") w++;
    else p++;
  }
  const t = escopo.length;
  const pctC = t > 0 ? Math.round((c / t) * 100) : 0;
  const pctW = t > 0 ? Math.round((w / t) * 100) : 0;
  const pctP = t > 0 ? Math.round((p / t) * 100) : 0;
  // % acerto = acertos / total (anuladas já contam como "correct" via
  // questaoStatus). Mesma regra do backend e do PerformanceModal.
  const pctAcerto = t > 0 ? Math.round((c / t) * 100) : null;
  return (
    // Grid 2x2 com EXATAMENTE o mesmo gap (gap-2) dos botões e células
    // do mesmo tamanho (h-10) — pra que summary e ações formem uma única
    // grade visual de 2x4 colunas no desktop quando ficam lado a lado.
    <div class="grid h-full grid-cols-2 grid-rows-2 gap-2 lg:min-w-[16rem]">
      <MiniStat tone="emerald" label="Acertos" value={c} pct={pctC} />
      <MiniStat tone="red" label="Erros" value={w} pct={pctW} />
      <MiniStat tone="muted" label="Pend." value={p} pct={pctP} />
      <MiniStat
        tone="primary"
        label="% Acerto"
        value={pctAcerto != null ? `${pctAcerto}%` : "—"}
      />
    </div>
  );
}

function MiniStat({
  tone,
  label,
  value,
  pct
}: {
  tone: "emerald" | "red" | "muted" | "primary";
  label: string;
  value: number | string;
  pct?: number;
}) {
  const toneClasses =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : tone === "red"
      ? "bg-red-500/10 text-red-600 dark:text-red-400"
      : tone === "primary"
      ? "bg-primary/10 text-primary"
      : "bg-muted/40 text-muted-foreground";
  return (
    <div
      class={`flex h-10 min-h-10 flex-col items-center justify-center gap-0.5 rounded-md border border-border px-2 py-1 ${toneClasses}`}
      title={`${label}${pct != null ? ` · ${pct}%` : ""}`}
    >
      <span class="text-[9px] font-bold uppercase tracking-wide opacity-80 leading-none">
        {label}
      </span>
      <span class="leading-none">
        <span class="text-sm font-bold text-foreground">{value}</span>
        {pct != null && (
          <span class="ml-1 text-[10px] font-medium opacity-70">
            · {pct}%
          </span>
        )}
      </span>
    </div>
  );
}

// Pílula flutuante de sync — mesma linguagem do #sync-floating do Simulador
// legado. Posição fixa bottom-left (longe do player de música em bottom-right),
// dot-only por padrão, expande revelando label por 1500ms ao mudar de estado
// e no hover/focus. Discreta, não compete com elementos da tela.
//
// Cores do dot:
//   pending → âmbar (mudanças não salvas)
//   saving  → indigo + pulse
//   saved   → verde (e expande pra "Salvo" por 1500ms)
//   error   → vermelho
function SyncIndicator() {
  const state = syncState.value;
  const [autoExpanded, setAutoExpanded] = useState(false);
  const lastStateRef = useRef<SyncState>("idle");

  // Auto-expandir por 1500ms quando o estado MUDA (e não é idle). Hover/focus
  // mantém expandido via CSS (group-hover/group-focus-within).
  useEffect(() => {
    if (state === lastStateRef.current) return;
    lastStateRef.current = state;
    if (state === "idle") {
      setAutoExpanded(false);
      return;
    }
    setAutoExpanded(true);
    const t = setTimeout(() => setAutoExpanded(false), 1500);
    return () => clearTimeout(t);
  }, [state]);

  if (state === "idle") return null;

  let label = "";
  let dotCls = "bg-muted-foreground";
  let pulse = false;
  if (state === "pending") {
    label = "Mudanças pendentes";
    dotCls = "bg-amber-500";
  } else if (state === "saving") {
    label = "Salvando…";
    dotCls = "bg-indigo-500";
    pulse = true;
  } else if (state === "saved") {
    label = "Salvo";
    dotCls = "bg-emerald-500";
  } else if (state === "error") {
    label = "Erro ao salvar";
    dotCls = "bg-red-500";
  }

  return (
    <div
      role="status"
      aria-label={label}
      title={label}
      tabIndex={0}
      class={`group fixed bottom-3 left-3 z-40 flex cursor-default items-center overflow-hidden rounded-full border border-border bg-card/90 text-[11px] font-semibold text-muted-foreground shadow-md backdrop-blur transition-[padding,gap] duration-300 ease-out focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
        autoExpanded
          ? "gap-1.5 px-3 py-1.5"
          : "gap-0 p-1.5 hover:gap-1.5 hover:px-3 hover:py-1.5 focus-visible:gap-1.5 focus-visible:px-3 focus-visible:py-1.5"
      }`}
    >
      <span
        class={`size-2 shrink-0 rounded-full ${dotCls} ${pulse ? "animate-pulse" : ""}`}
      />
      <span
        class={`whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-300 ease-out ${
          autoExpanded
            ? "max-w-[180px] opacity-100"
            : "max-w-0 opacity-0 group-hover:max-w-[180px] group-hover:opacity-100 group-focus-visible:max-w-[180px] group-focus-visible:opacity-100"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

// =====================
// Filter sidebar (E5)
// =====================

// Painel de filtros sempre como overlay (mobile + desktop). Aberto via botão
// no StudyHeader. Mantém-se sobre a área central — questão e lista ficam
// onde estão.
export const filtersOpen = signal<boolean>(false);

function FilterPanel({ data }: { data: BankStudyResponse }) {
  const isOpen = filtersOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (filtersOpen.value = false)
  );
  if (!isOpen) return null;
  const filtros = data.estudo.filtros;
  const activeCount = activeFilterCount(filtros);
  // Derivar temas e fontes únicos do banco (mantém ordem de primeira ocorrência).
  const temas: string[] = [];
  const fontes: string[] = [];
  for (const q of data.questoes) {
    if (q.tema && !temas.includes(q.tema)) temas.push(q.tema);
    if (q.fonte && !fontes.includes(q.fonte)) fontes.push(q.fonte);
  }

  // Counts úteis no sidebar: quantas em cada status (com OUTROS filtros aplicados)
  // — espelha o "Acertos / Erros / Pendentes" do legado.
  const statusCounts = { correct: 0, wrong: 0, pending: 0 };
  for (const q of data.questoes) {
    if (!matchesFiltro(
      q,
      { ...filtros, status: [] },
      data.estudo.respostas
    )) continue;
    statusCounts[questaoStatus(q, data.estudo.respostas)]++;
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) requestClose();
  }

  return (
    <div
      class="fixed inset-0 z-[65] grid items-end bg-black/40 backdrop-blur-sm sm:items-stretch"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Filtros"
    >
      <aside
        ref={ref}
        class="ml-auto flex max-h-[85vh] w-full flex-col overflow-y-auto scroll-thin rounded-t-lg border border-border bg-card p-4 shadow-2xl sm:max-h-full sm:max-w-sm sm:rounded-none sm:border-l sm:border-x-0 sm:border-y-0"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="mb-4 flex items-center justify-between">
          <h2 class="flex items-center gap-2 text-sm font-bold">
            <SlidersHorizontal width="1.3em" height="1.3em" className="text-primary" />
            Filtros
            {activeCount > 0 && (
              <span class="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-bold text-primary">
                {activeCount}
              </span>
            )}
          </h2>
          <div class="flex items-center gap-1.5">
            {activeCount > 0 && (
              <button
                type="button"
                onClick={clearAllFilters}
                class="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <RotateCcw width="1.3em" height="1.3em" /> Limpar
              </button>
            )}
            <button
              type="button"
              aria-label="Fechar"
              onClick={requestClose}
              class="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          </div>
        </header>
        <div class="space-y-4">

        {/* Busca — flex layout: lupa fica DENTRO do campo */}
        <label class="flex items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-primary">
          <Search width="1.3em" height="1.3em" className="shrink-0 text-muted-foreground" strokeWidth={2} />
          <input
            type="search"
            value={filtros.busca}
            onInput={(e: Event) =>
              updateFiltros({ busca: (e.target as HTMLInputElement).value })
            }
            placeholder="Buscar no enunciado…"
            class="min-w-0 flex-1 bg-transparent py-1.5 text-xs placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </label>

        {/* Status — radio (single-select): ou tudo, ou só um. */}
        <FilterSection title="Status">
          <StatusRadio
            label="Tudo"
            count={statusCounts.correct + statusCounts.wrong + statusCounts.pending}
            active={filtros.status.length === 0}
            onClick={() => updateFiltros({ status: [] })}
          />
          <StatusRadio
            label="Acertos"
            count={statusCounts.correct}
            tone="emerald"
            icon={CircleCheck}
            active={filtros.status.length === 1 && filtros.status[0] === "correct"}
            onClick={() => updateFiltros({ status: ["correct"] })}
          />
          <StatusRadio
            label="Erros"
            count={statusCounts.wrong}
            tone="red"
            icon={CircleX}
            active={filtros.status.length === 1 && filtros.status[0] === "wrong"}
            onClick={() => updateFiltros({ status: ["wrong"] })}
          />
          <StatusRadio
            label="Pendentes"
            count={statusCounts.pending}
            tone="muted"
            icon={CircleDashed}
            active={filtros.status.length === 1 && filtros.status[0] === "pending"}
            onClick={() => updateFiltros({ status: ["pending"] })}
          />
        </FilterSection>

        {/* Dificuldade — array vazio = todas marcadas (sem filtro) */}
        <FilterSection title="Dificuldade">
          {[1, 2, 3, 4, 5].map((d) => (
            <FilterRow
              key={d}
              label={
                <span class="inline-flex items-center gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <StarSolid key={i} filled={i < d} size={11} />
                  ))}
                </span>
              }
              checked={filtros.dificuldade.length === 0 || filtros.dificuldade.includes(d)}
              onToggle={() => toggleDificuldade(d)}
            />
          ))}
        </FilterSection>

        {/* Tipo de conteúdo */}
        <FilterSection title="Conteúdo">
          <FilterRow
            label={
              <span class="inline-flex items-center gap-1.5">
                <AlignLeft width="1.3em" height="1.3em" className="text-sky-500" /> Com situação
              </span>
            }
            checked={filtros.somenteCaso}
            onToggle={() => updateFiltros({ somenteCaso: !filtros.somenteCaso })}
          />
          <FilterRow
            label={
              <span class="inline-flex items-center gap-1.5">
                <ImageIcon width="1.3em" height="1.3em" className="text-violet-500" /> Com imagem
              </span>
            }
            checked={filtros.somenteImagem}
            onToggle={() => updateFiltros({ somenteImagem: !filtros.somenteImagem })}
          />
          <FilterRow
            label={
              <span class="inline-flex items-center gap-1.5">
                <TableIcon width="1.3em" height="1.3em" className="text-teal-500" /> Com tabela
              </span>
            }
            checked={filtros.somenteTabela}
            onToggle={() => updateFiltros({ somenteTabela: !filtros.somenteTabela })}
          />
        </FilterSection>

        {/* Tipo de questão — só aparece quando o banco tem mais de um tipo
            (banco com 100% ME ou 100% dissertativa não precisa do filtro). */}
        <TipoFilterSection filtros={filtros} />

        {/* Tema — array vazio = todas marcadas. Lista flui sem scroll
            interno; o painel inteiro rola se precisar. */}
        {temas.length > 0 && (
          <FilterSection title={`Tema (${temas.length})`}>
            <div class="space-y-0.5">
              {temas.map((t) => (
                <FilterRow
                  key={t}
                  label={t}
                  checked={filtros.temas.length === 0 || filtros.temas.includes(t)}
                  onToggle={() => toggleTema(t)}
                />
              ))}
            </div>
          </FilterSection>
        )}

        {/* Fonte — array vazio = todas marcadas. Sem scroll interno. */}
        {fontes.length > 0 && (
          <FilterSection title={`Fonte (${fontes.length})`}>
            <div class="space-y-0.5">
              {fontes.map((f) => (
                <FilterRow
                  key={f}
                  label={f}
                  checked={filtros.fontes.length === 0 || filtros.fontes.includes(f)}
                  onToggle={() => toggleFonte(f)}
                />
              ))}
            </div>
          </FilterSection>
        )}
        </div>
      </aside>
    </div>
  );
}


// Radio (single-select) — pra status onde "ou tudo, ou só uma categoria".
// Diferente do FilterRow (checkbox), aqui só uma linha fica ativa por vez.
export function StatusRadio({
  label,
  count,
  tone,
  icon: Icon,
  active,
  onClick
}: {
  label: string;
  count?: number;
  tone?: "emerald" | "red" | "muted";
  icon?: typeof CircleCheck;
  active: boolean;
  onClick: () => void;
}) {
  const iconColor =
    tone === "emerald"
      ? "text-emerald-500"
      : tone === "red"
        ? "text-red-500"
        : "text-muted-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
        active
          ? "bg-primary/10 text-foreground"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      <span class="flex min-w-0 flex-1 items-center gap-1.5">
        <span
          class={`grid size-3.5 shrink-0 place-items-center rounded-full border ${
            active ? "border-primary bg-primary" : "border-border bg-background"
          }`}
          aria-hidden
        >
          {active && <span class="size-1.5 rounded-full bg-white" />}
        </span>
        {Icon && (
          <Icon width="1.1em" height="1.1em" className={iconColor} strokeWidth={2.2} />
        )}
        <span class="truncate">{label}</span>
      </span>
      {count != null && (
        <span class="shrink-0 rounded-full bg-accent px-1.5 py-0.5 text-[10px] font-bold tabular-nums">
          {count}
        </span>
      )}
    </button>
  );
}

export function FilterSection({ title, children }: { title: string; children: any }) {
  return (
    <section>
      <h3 class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div class="space-y-0.5">{children}</div>
    </section>
  );
}

// Filtro por tipo de questão (mc / essay). Só aparece quando o banco tem
// pelo menos um exemplar de cada tipo — não faz sentido filtrar quando há
// uma opção só. Lista vazia (default) = todos os tipos visíveis.
function TipoFilterSection({
  filtros
}: {
  filtros: BankStudyResponse["estudo"]["filtros"];
}) {
  const ws = studyWorkspace.value;
  if (!ws?.data) return null;
  const tipos = uniqueTiposOf(ws.data.questoes);
  if (tipos.length < 2) return null;
  const selected = filtros.tipos ?? [];
  return (
    <FilterSection title="Tipo de questão">
      <FilterRow
        label={
          <span class="inline-flex items-center gap-1.5">
            <span class="rounded-md bg-blue-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300">
              ME
            </span>
            Múltipla escolha
          </span>
        }
        checked={selected.length === 0 || selected.includes("mc")}
        onToggle={() => toggleTipo("mc")}
      />
      <FilterRow
        label={
          <span class="inline-flex items-center gap-1.5">
            <span class="rounded-md bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              DISS
            </span>
            Dissertativa
          </span>
        }
        checked={selected.length === 0 || selected.includes("essay")}
        onToggle={() => toggleTipo("essay")}
      />
    </FilterSection>
  );
}

// =====================
// Mapa de questões (E7)
// =====================
// Grid clicável de TODAS as questões filtradas. Cada célula colorida por
// status (acerto verde, erro vermelho, pendente cinza, anulada âmbar).
// Indicadores de tipo (caso/imagem/tabela) são dots coloridos pequenos
// dentro da célula (sem ícones — não cabem em 56px). A legenda no header
// usa as MESMAS cores pra consistência visual.
// Clique pula pra questão; atalho M abre/fecha.

function QuestionMapModal({ data, currentIdx }: { data: BankStudyResponse; currentIdx: number }) {
  const minhas = getMinhasQuestoes(data);
  const isOpen = mapOpen.value;
  const { ref, animStyle, onAnimationEnd, requestClose, isClosing } = useModalGrowShrink(
    isOpen,
    () => (mapOpen.value = false)
  );
  // showGabarito: revela a letra correta em cada célula
  const showGabarito = useRef(false);
  const _forceTick = signal(0); // pra forçar rerender quando showGabarito mudar
  const _ = _forceTick.value; // marca como lido pra signals reagirem

  function rerender() {
    _forceTick.value++;
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) requestClose();
  }

  function pickIdx(idx: number) {
    // Sem animação — usuário pulou pra outra questão.
    gotoIdx(idx);
    mapOpen.value = false;
  }

  function toggleGabarito() {
    showGabarito.current = !showGabarito.current;
    rerender();
  }

  // Sumário: contagem por status + por tipo de conteúdo no escopo filtrado
  let nC = 0, nW = 0, nP = 0, nA = 0;
  let nCaso = 0, nImg = 0, nTab = 0;
  for (const q of minhas) {
    if (isAnulada(q)) nA++;
    const st = questaoStatus(q, data.estudo.respostas);
    if (st === "correct") nC++;
    else if (st === "wrong") nW++;
    else nP++;
    if (q.caso) nCaso++;
    if (questaoHasImages(q)) nImg++;
    if (questaoHasTabelas(q)) nTab++;
  }

  return (
    <div
      class="fixed inset-0 z-[70] grid min-h-full place-items-start overflow-y-auto scroll-thin bg-black/60 p-4 backdrop-blur-sm sm:place-items-center"
      style={{ animation: isClosing ? "modal-backdrop-out 180ms forwards" : "modal-backdrop-in 200ms" }}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Mapa de questões"
    >
      <div
        ref={ref}
        class="w-full max-w-4xl overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        style={animStyle}
        onAnimationEnd={onAnimationEnd}
      >
        {/* Header */}
        <header class="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div class="flex min-w-0 items-start gap-3">
            <div class="grid size-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/20">
              <Grid3x3 width="1.3em" height="1.3em" />
            </div>
            <div class="min-w-0 space-y-1.5">
              <h2 class="text-base font-bold">Mapa de questões</h2>
              <div class="text-xs text-muted-foreground">
                {minhas.length} questões visíveis
              </div>
              {/* Legenda: status + tipo, com dots coloridos */}
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <MapLegendChip color="bg-emerald-500" label={`${nC} acertos`} />
                <MapLegendChip color="bg-red-500" label={`${nW} erros`} />
                <MapLegendChip color="bg-muted-foreground/40" label={`${nP} pendentes`} />
                {nA > 0 && (
                  <MapLegendChip color="bg-amber-500" label={`${nA} anuladas`} />
                )}
                <span class="h-3 w-px bg-border" aria-hidden />
                {nCaso > 0 && (
                  <MapLegendIcon
                    Icon={AlignLeft}
                    iconClass="text-sky-500"
                    label={`${nCaso} situação`}
                  />
                )}
                {nImg > 0 && (
                  <MapLegendIcon
                    Icon={ImageIcon}
                    iconClass="text-violet-500"
                    label={`${nImg} imagem`}
                  />
                )}
                {nTab > 0 && (
                  <MapLegendIcon
                    Icon={TableIcon}
                    iconClass="text-teal-500"
                    label={`${nTab} tabela`}
                  />
                )}
              </div>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2 self-start">
            <button
              type="button"
              onClick={toggleGabarito}
              aria-pressed={showGabarito.current}
              title="Mostra a letra correta em cada questão"
              class={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                showGabarito.current
                  ? "border-primary bg-primary text-white"
                  : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
              }`}
            >
              <Eye width="1.3em" height="1.3em" strokeWidth={2} />
              <span class="hidden sm:inline">
                {showGabarito.current ? "Ocultar gabarito" : "Ver gabarito"}
              </span>
            </button>
            <button
              type="button"
              aria-label="Fechar"
              onClick={requestClose}
              class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        </header>

        {/* Grid */}
        {minhas.length === 0 ? (
          <div class="px-6 py-10 text-center text-sm text-muted-foreground">
            Nenhuma questão bate com os filtros atuais.
          </div>
        ) : (
          <div class="max-h-[60vh] overflow-y-auto scroll-thin px-6 py-5">
            <div class="grid grid-cols-5 gap-2 lg:grid-cols-10">
              {minhas.map((q, i) => (
                <MapCell
                  key={q.id}
                  q={q}
                  num={i + 1}
                  isCurrent={i === currentIdx}
                  status={questaoStatus(q, data.estudo.respostas)}
                  anulada={isAnulada(q)}
                  showGabarito={showGabarito.current}
                  onClick={() => pickIdx(i)}
                />
              ))}
            </div>
          </div>
        )}

        <footer class="border-t border-border bg-card/50 px-6 py-2.5 text-right text-[11px] text-muted-foreground">
          <kbd class="rounded bg-accent px-1.5 py-0.5 font-mono">M</kbd> abre/fecha &middot;
          <kbd class="ml-1 rounded bg-accent px-1.5 py-0.5 font-mono">Esc</kbd> fecha
        </footer>
      </div>
    </div>
  );
}

function MapLegendChip({
  color,
  label,
  small
}: {
  color: string;
  label: string;
  small?: boolean;
}) {
  return (
    <span class="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
      <span
        class={`inline-block rounded-full ${color} ${small ? "size-1.5" : "size-2"}`}
      />
      {label}
    </span>
  );
}

function MapLegendIcon({
  Icon,
  iconClass,
  label
}: {
  Icon: typeof AlignLeft;
  iconClass: string;
  label: string;
}) {
  return (
    <span class="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
      <Icon width="1.3em" height="1.3em" className={iconClass} strokeWidth={2} />
      {label}
    </span>
  );
}

function MapCell({
  q,
  num,
  isCurrent,
  status,
  anulada,
  showGabarito,
  onClick
}: {
  q: QuestaoStudy;
  num: number;
  isCurrent: boolean;
  status: QStatus;
  anulada: boolean;
  showGabarito: boolean;
  onClick: () => void;
}) {
  // Cor base por status
  let toneClasses = "border-border bg-card/60 hover:border-primary/50 hover:bg-accent/60";
  if (anulada) {
    toneClasses =
      "border-amber-500/40 bg-amber-500/15 text-amber-700 hover:border-amber-500/60 dark:text-amber-300";
  } else if (status === "correct") {
    toneClasses =
      "border-emerald-500/40 bg-emerald-500/15 text-emerald-700 hover:border-emerald-500/60 dark:text-emerald-300";
  } else if (status === "wrong") {
    toneClasses =
      "border-red-500/40 bg-red-500/15 text-red-700 hover:border-red-500/60 dark:text-red-300";
  }

  // Indicadores de tipo (caso/imagem/tabela) — dots coloridos pequenos,
  // não ícones (não cabem em células 56px). Cores casam com a legenda:
  // azul=caso, violeta=imagem, teal=tabela.
  const hasCaso = !!q.caso;
  const hasImg = questaoHasImages(q);
  const hasTab = questaoHasTabelas(q);

  // Letra do gabarito (A=0, B=1, ...) — só renderiza se showGabarito ativo
  const gabaritoLetra = !anulada && correctIdx(q) >= 0
    ? String.fromCharCode(65 + correctIdx(q))
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      title={`Questão ${num}${q.tema ? ` · ${q.tema}` : ""}`}
      class={`relative flex aspect-square min-h-[56px] flex-col items-center justify-center rounded-md border text-sm font-bold transition-all ${toneClasses} ${
        isCurrent ? "ring-2 ring-primary scale-105" : ""
      }`}
    >
      <span class="leading-none">{num}</span>
      {showGabarito && gabaritoLetra && (
        <span class="mt-0.5 rounded bg-primary/20 px-1 text-[9px] font-bold text-primary">
          {gabaritoLetra}
        </span>
      )}
      {/* Mini-pílulas tipadas: fundo colorido sutil deixa cada cor reconhecível
          mesmo em tamanho pequeno, sem poluição visual. Sky=Situação,
          Violet=Imagem, Teal=Tabela (mesma paleta dos badges do card). */}
      <span class="mt-1 flex items-center gap-0.5">
        {hasCaso && (
          <span
            title="Situação"
            class="grid size-3 place-items-center rounded-[3px] bg-sky-500/25 text-sky-600 dark:bg-sky-500/30 dark:text-sky-300"
          >
            <AlignLeft width="8" height="8" strokeWidth={2.6} />
          </span>
        )}
        {hasImg && (
          <span
            title="Imagem"
            class="grid size-3 place-items-center rounded-[3px] bg-violet-500/25 text-violet-600 dark:bg-violet-500/30 dark:text-violet-300"
          >
            <ImageIcon width="8" height="8" strokeWidth={2.6} />
          </span>
        )}
        {hasTab && (
          <span
            title="Tabela"
            class="grid size-3 place-items-center rounded-[3px] bg-teal-500/25 text-teal-600 dark:bg-teal-500/30 dark:text-teal-300"
          >
            <TableIcon width="8" height="8" strokeWidth={2.6} />
          </span>
        )}
      </span>
    </button>
  );
}

// =====================
// Progress bars (E6) — espelha updateScore do legado
// =====================
// Contagens respeitam o escopo (filtros sem o de status), pra mostrar a
// distribuição real do que o aluno está estudando agora. Cada barra é um
// botão que ativa/desativa o filtro de status correspondente.

function ProgressBars({ data }: { data: BankStudyResponse }) {
  const filtros = data.estudo.filtros;
  // Escopo = todas as questões que passam pelos filtros (EXCETO status)
  const escopo = data.questoes.filter((q) =>
    matchesFiltro(q, { ...filtros, status: [] }, data.estudo.respostas)
  );
  const total = escopo.length;
  let nCorrect = 0;
  let nWrong = 0;
  let nPending = 0;
  for (const q of escopo) {
    const st = questaoStatus(q, data.estudo.respostas);
    if (st === "correct") nCorrect++;
    else if (st === "wrong") nWrong++;
    else nPending++;
  }
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);

  if (total === 0) return null;

  return (
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <ProgressPill
        label="Acertos"
        count={nCorrect}
        pct={pct(nCorrect)}
        active={filtros.status.includes("correct")}
        onToggle={() => toggleStatus("correct")}
        toneBar="bg-emerald-500"
        toneBg="bg-emerald-500/10 hover:bg-emerald-500/20"
        toneText="text-emerald-600 dark:text-emerald-400"
        toneActive="ring-emerald-500/60"
      />
      <ProgressPill
        label="Erros"
        count={nWrong}
        pct={pct(nWrong)}
        active={filtros.status.includes("wrong")}
        onToggle={() => toggleStatus("wrong")}
        toneBar="bg-red-500"
        toneBg="bg-red-500/10 hover:bg-red-500/20"
        toneText="text-red-600 dark:text-red-400"
        toneActive="ring-red-500/60"
      />
      <ProgressPill
        label="Pendentes"
        count={nPending}
        pct={pct(nPending)}
        active={filtros.status.includes("pending")}
        onToggle={() => toggleStatus("pending")}
        toneBar="bg-muted-foreground/50"
        toneBg="bg-muted/40 hover:bg-muted/70"
        toneText="text-muted-foreground"
        toneActive="ring-muted-foreground/40"
      />
    </div>
  );
}

function ProgressPill({
  label,
  count,
  pct,
  active,
  onToggle,
  toneBar,
  toneBg,
  toneText,
  toneActive
}: {
  label: string;
  count: number;
  pct: number;
  active: boolean;
  onToggle: () => void;
  toneBar: string;
  toneBg: string;
  toneText: string;
  toneActive: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      class={`group relative overflow-hidden rounded-md border border-border px-3 py-2 text-left transition-all ${toneBg} ${
        active ? `ring-2 ${toneActive}` : ""
      }`}
    >
      <div class="flex items-baseline justify-between gap-2">
        <span class={`text-[10px] font-bold uppercase tracking-wider ${toneText}`}>
          {label}
        </span>
        <span class="text-xs font-bold text-foreground">
          {count}
          <span class="ml-1 text-[10px] font-medium text-muted-foreground">· {pct}%</span>
        </span>
      </div>
      <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-background">
        <div
          class={`h-full ${toneBar} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </button>
  );
}

export function FilterRow({
  label,
  count,
  icon: Icon,
  iconClass,
  checked,
  onToggle
}: {
  label: any;
  count?: number;
  icon?: typeof CircleCheck;
  iconClass?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      class={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
        checked
          ? "bg-primary/10 text-foreground"
          : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
      }`}
    >
      <span class="flex min-w-0 flex-1 items-center gap-1.5">
        <span
          class={`grid size-3.5 shrink-0 place-items-center rounded-sm border ${
            checked ? "border-primary bg-primary text-white" : "border-border bg-background"
          }`}
          aria-hidden
        >
          {checked && (
            <svg viewBox="0 0 16 16" class="size-2.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3.5 8.5 6.5 11.5 12.5 4.5" />
            </svg>
          )}
        </span>
        {Icon && (
          <Icon
            width="1.3em"
            height="1.3em"
            className={`shrink-0 ${iconClass ?? ""}`}
            strokeWidth={1.75}
          />
        )}
        <span class="truncate">{label}</span>
      </span>
      {count != null && (
        <span class="shrink-0 rounded bg-accent px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

function EmptyState() {
  return (
    <div class="rounded-md border border-border bg-card p-8 text-center text-sm text-muted-foreground">
      Esse banco não tem questões.
    </div>
  );
}

function QuestionView({ q, selected }: { q: QuestaoStudy; selected: number | undefined }) {
  const anulada = isAnulada(q);
  const corrIdx = correctIdx(q);
  const answered = selected !== undefined;
  const acertou = answered && isAcerto(q, selected);
  const articleRef = useRef<HTMLElement>(null);
  // KaTeX render: carrega a lib (uma vez) e renderiza math no <article>
  // sempre que a questão muda OU o estado de resposta muda (a explicação
  // pode aparecer agora e ter math).
  useEffect(() => {
    void ensureKaTeX().then(() => renderMathIn(articleRef.current));
  }, [q.id, answered, anulada]);
  return (
    <article ref={articleRef} class="space-y-4 rounded-md border border-border bg-card p-6">
      {/* Cabeçalho da questão: identificadores + tipo + dificuldade
          Linguagem visual igual ao mapa de questões — ícones e cores consistentes.
          Ícones de caso (Estetoscópio), imagem (Image), tabela (Table) ficam aqui
          em cima, não no rodapé. Dificuldade vira estrelas (preenchidas até N). */}
      <div class="flex flex-wrap items-center gap-2 text-[11px]">
        {q.numero_original != null && (
          <span class={CHIP_BASE + " bg-accent font-mono text-muted-foreground"}>
            #{String(q.numero_original)}
          </span>
        )}
        {q.tema && (
          <span class={CHIP_BASE + " bg-primary/10 font-medium text-primary"}>
            {q.tema}
          </span>
        )}
        {q.fonte && (
          <span class={CHIP_BASE + " bg-accent text-muted-foreground"}>
            {q.fonte}
          </span>
        )}
        {q.dificuldade != null && q.dificuldade >= 1 && (
          <DifficultyStars value={Number(q.dificuldade)} />
        )}
        {q.caso && (
          <span
            title="Contém situação (texto/caso clínico que antecede a pergunta)"
            class={CHIP_BASE + " gap-1 bg-sky-500/15 font-medium text-sky-600 dark:text-sky-400"}
          >
            <AlignLeft width="1.3em" height="1.3em" /> Situação
          </span>
        )}
        {questaoHasImages(q) && (
          <span
            title="Contém imagem"
            class={CHIP_BASE + " gap-1 bg-violet-500/15 font-medium text-violet-600 dark:text-violet-400"}
          >
            <ImageIcon width="1.3em" height="1.3em" /> Imagem
          </span>
        )}
        {questaoHasTabelas(q) && (
          <span
            title="Contém tabela"
            class={CHIP_BASE + " gap-1 bg-teal-500/15 font-medium text-teal-600 dark:text-teal-400"}
          >
            <TableIcon width="1.3em" height="1.3em" /> Tabela
          </span>
        )}
        {anulada && (
          <span class={CHIP_BASE + " bg-amber-500/20 font-medium text-amber-600 dark:text-amber-400"}>
            Anulada
          </span>
        )}
      </div>

      {/* Situação (caso clínico) — texto que antecede a pergunta */}
      {q.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            <AlignLeft width="1.3em" height="1.3em" />
            Situação
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.caso) }}
          />
        </div>
      )}

      {/* Tabelas (opcional) */}
      {Array.isArray(q.tabelas) && q.tabelas.length > 0 && (
        <div class="space-y-3">
          {q.tabelas.map((t, i) => (
            <QuestionTable key={i} table={t} />
          ))}
        </div>
      )}

      {/* Imagens (opcional) */}
      {(() => {
        const imgs: Array<{ url: string; legenda?: string }> = [];
        if (typeof q.imagem_url === "string" && q.imagem_url) {
          imgs.push({ url: q.imagem_url });
        }
        if (Array.isArray(q.imagens)) {
          for (const it of q.imagens) {
            if (typeof it === "string") imgs.push({ url: it });
            else if (it && typeof it === "object" && typeof it.url === "string") {
              imgs.push({ url: it.url, legenda: it.legenda });
            }
          }
        }
        if (imgs.length === 0) return null;
        // Centraliza lateralmente. Quando há 1 imagem, ela ocupa até 480px
        // de largura. Quando há 2+, vai pra grid 2 colunas, mas o container
        // continua centralizado (mx-auto).
        const single = imgs.length === 1;
        return (
          <div
            class={`mx-auto grid gap-3 ${
              single ? "max-w-md" : "max-w-2xl sm:grid-cols-2"
            }`}
          >
            {imgs.map((im, i) => (
              <figure
                key={i}
                class="overflow-hidden rounded-md border border-border bg-accent/20"
              >
                <button
                  type="button"
                  onClick={() => (lightboxImg.value = im)}
                  class="block w-full text-left transition-opacity hover:opacity-90"
                  title="Clique pra ampliar"
                >
                  <img
                    src={im.url}
                    alt={im.legenda ?? `Imagem ${i + 1}`}
                    class="mx-auto block w-full cursor-zoom-in"
                    loading="lazy"
                  />
                </button>
                {im.legenda && (
                  <figcaption
                    class="border-t border-border bg-card/60 px-3 py-2 text-center text-xs text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: renderInline(im.legenda) }}
                  />
                )}
              </figure>
            ))}
          </div>
        );
      })()}

      {/* Enunciado */}
      <div
        class="prose-question text-base leading-relaxed"
        dangerouslySetInnerHTML={{ __html: renderBlock(q.pergunta) }}
      />

      {/* Alternativas */}
      <ul class="space-y-2">
        {q.opcoes.map((opt, i) => {
          const letter = String.fromCharCode(65 + i); // A, B, C, ...
          const isSelected = selected === i;
          const isCorrectAlt = i === corrIdx;
          // Regras do legado:
          // - Antes de responder: estado neutro (clicável, sem cor)
          // - Após responder em estudo:
          //     - selecionada certa  → verde forte (selected-correct)
          //     - selecionada errada → vermelha (selected-wrong)
          //     - correta não selecionada (quando errou) → verde pálido (is-correct-unanswered)
          // - Anulada: tudo desabilitado, dica visual de "neutro"
          let classes = "border-border bg-card/60 hover:bg-accent/60";
          let badgeClasses = "border-border bg-background";
          let trailing: any = null;
          if (anulada) {
            classes = "border-border bg-card/30 cursor-not-allowed opacity-60";
            badgeClasses = "border-border bg-background";
          } else if (answered) {
            if (isSelected && acertou) {
              classes =
                "border-emerald-500/60 bg-emerald-500/15 ring-1 ring-emerald-500/40";
              badgeClasses =
                "border-emerald-500/60 bg-emerald-500 text-white";
              trailing = <Check className="size-4 text-emerald-500" />;
            } else if (isSelected && !acertou) {
              classes =
                "border-red-500/60 bg-red-500/15 ring-1 ring-red-500/40";
              badgeClasses = "border-red-500/60 bg-red-500 text-white";
              trailing = <XIcon className="size-4 text-red-500" />;
            } else if (!isSelected && isCorrectAlt) {
              classes =
                "border-emerald-500/30 bg-emerald-500/5 ring-1 ring-emerald-500/20";
              badgeClasses =
                "border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
              trailing = <Check className="size-4 text-emerald-500/70" />;
            } else {
              classes = "border-border bg-card/30 opacity-70";
            }
          }
          return (
            <li key={i}>
              <button
                type="button"
                disabled={anulada}
                onClick={() => setRespostaForCurrent(i)}
                aria-pressed={isSelected}
                class={`flex w-full ${
                  opt && typeof opt === "object" && "imagem" in (opt as Record<string, unknown>)
                    ? "items-start"
                    : "items-center"
                } gap-3 rounded-md border px-4 py-3 text-left text-sm transition-colors ${classes}`}
              >
                <span
                  class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}
                >
                  {letter}
                </span>
                {opt && typeof opt === "object" && "imagem" in (opt as Record<string, unknown>) ? (
                  <span class="flex min-w-0 flex-1 flex-col gap-1">
                    <img
                      src={(opt as { imagem: string }).imagem}
                      alt={(opt as { legenda?: string }).legenda ?? "Alternativa"}
                      loading="lazy"
                      onClick={(e) => {
                        e.stopPropagation();
                        lightboxImg.value = {
                          image: (opt as { imagem: string }).imagem
                        } as any;
                      }}
                      class="block max-h-64 w-auto cursor-zoom-in rounded-md border border-border bg-background object-contain"
                    />
                    {(opt as { legenda?: string }).legenda && (
                      <span
                        class="text-[11px] text-muted-foreground"
                        dangerouslySetInnerHTML={{
                          __html: renderInline((opt as { legenda?: string }).legenda ?? "")
                        }}
                      />
                    )}
                  </span>
                ) : (
                  <span
                    class="flex-1 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: renderInline(String(opt ?? "")) }}
                  />
                )}
                {trailing && <span class="shrink-0">{trailing}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Feedback de status + Limpar */}
      {answered && !anulada && (
        <div class="flex items-center justify-between gap-3 rounded-md border border-border bg-accent/20 px-4 py-2 text-sm">
          <span class={`font-medium ${acertou ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
            {acertou ? "Você acertou!" : "Resposta incorreta."}
          </span>
          <button
            type="button"
            onClick={clearCurrentResposta}
            class="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <RotateCcw width="1.3em" height="1.3em" /> Limpar resposta
          </button>
        </div>
      )}

      {/* Explicação — visível se respondeu OU se anulada (legado também
          revela a explicação fora de prova em qs anuladas) */}
      {(answered || anulada) && q.explicacao && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info width="1.3em" height="1.3em" />
            Explicação
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderBlock(q.explicacao) }}
          />
        </div>
      )}

    </article>
  );
}

// Classe-base compartilhada por TODOS os chips do header de questão.
// Altura fixa (h-6) garante alinhamento consistente, mesmo quando o
// conteúdo é icon-only (ex.: estrelas de dificuldade).
const CHIP_BASE =
  "inline-flex h-6 items-center rounded px-2 leading-none whitespace-nowrap";

// Estrelas de dificuldade (1-5). Preenchidas amarelo até N, vazias depois.
// Usamos SVG direto pra garantir que a parte interna fique sólida amarelo —
// Lucide Star às vezes não preenche dependendo de como o `fill` propaga.
function StarSolid({ filled, size = 14 }: { filled: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "#f59e0b" : "none"}
      stroke={filled ? "#f59e0b" : "currentColor"}
      stroke-width="2"
      stroke-linejoin="round"
      stroke-linecap="round"
      class={filled ? "" : "text-muted-foreground/50"}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function DifficultyStars({ value }: { value: number }) {
  const n = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span title={`Dificuldade ${n}/5`} class={CHIP_BASE + " gap-0.5 bg-accent"}>
      {[0, 1, 2, 3, 4].map((i) => (
        <StarSolid key={i} filled={i < n} size={12} />
      ))}
    </span>
  );
}

// Tabela "best-effort": aceita os formatos comuns do banco do legado.
// {titulo, cabecalho: string[], linhas: string[][], nota}
export function QuestionTable({ table }: { table: unknown }) {
  if (!table || typeof table !== "object") return null;
  const t = table as {
    titulo?: string;
    cabecalho?: string[];
    linhas?: string[][];
    nota?: string;
  };
  return (
    <div class="overflow-x-auto rounded-md border border-border bg-card/60">
      {t.titulo && (
        <div
          class="border-b border-border bg-accent/20 px-3 py-2 text-xs font-bold"
          dangerouslySetInnerHTML={{ __html: renderInline(t.titulo) }}
        />
      )}
      <table class="w-full text-sm">
        {Array.isArray(t.cabecalho) && t.cabecalho.length > 0 && (
          <thead>
            <tr class="border-b border-border bg-accent/10">
              {t.cabecalho.map((h, i) => (
                <th
                  key={i}
                  class="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: renderInline(h) }}
                />
              ))}
            </tr>
          </thead>
        )}
        {Array.isArray(t.linhas) && t.linhas.length > 0 && (
          <tbody>
            {t.linhas.map((row, i) => (
              <tr key={i} class="border-b border-border last:border-b-0">
                {row.map((cell, j) => (
                  <td
                    key={j}
                    class="px-3 py-2"
                    dangerouslySetInnerHTML={{ __html: renderInline(cell) }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {t.nota && (
        <div
          class="border-t border-border bg-card/40 px-3 py-2 text-[11px] italic text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: renderInline(t.nota) }}
        />
      )}
    </div>
  );
}

function NavigationBar({ currentIdx, total }: { currentIdx: number; total: number }) {
  const isFirst = currentIdx <= 0;
  const isLast = currentIdx >= total - 1;
  return (
    <nav class="sticky bottom-0 flex items-center justify-between gap-3 rounded-md border border-border bg-card/95 px-4 py-3 backdrop-blur-md">
      <button
        type="button"
        onClick={gotoPrev}
        disabled={isFirst}
        class="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <ChevronLeft width="1.3em" height="1.3em" />
        Anterior
      </button>
      <button
        type="button"
        onClick={gotoNext}
        disabled={isLast}
        class="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        Próxima
        <ChevronRight width="1.3em" height="1.3em" />
      </button>
    </nav>
  );
}
